import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled, { createGlobalStyle } from 'styled-components';
import { Flex, Box } from '@rebass/grid';
import { Tooltip, Text, AirlineLogo, IconFlex, DotSeparator, HotelThumbnail, CarVendorLogo, Card, Avatar, Badge, Checkbox, Popover, Button, creditCardByKey, CreditCardLogo, AirportCode as AirportCode$1, Modal } from '@amtrav/view-components';
import { getThemeProp, MatchMedia, media, lighten } from '@amtrav/themes';
import { getShortDateSummary, getTimeDisplayText } from '@amtrav/travel-search';
import { parseDate, dayOfWeekShortFormatter, monthShortFormatter, dayOfMonthFormatter, hourMinuteFormat, meridianFormat } from '@amtrav/dates';
import { IconArrowUpLong, IconLine, IconCreditCard, IconWarningTriangle, IconNext } from '@amtrav/icons';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function () {
  __assign = Object.assign || function __assign(t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
function __read(o, n) {
  var m = typeof Symbol === "function" && o[Symbol.iterator];
  if (!m) return o;
  var i = m.call(o),
    r,
    ar = [],
    e;
  try {
    while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
  } catch (error) {
    e = {
      error: error
    };
  } finally {
    try {
      if (r && !r.done && (m = i["return"])) m.call(i);
    } finally {
      if (e) throw e.error;
    }
  }
  return ar;
}
function __spreadArray(to, from, pack) {
  if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
    if (ar || !(i in from)) {
      if (!ar) ar = Array.prototype.slice.call(from, 0, i);
      ar[i] = from[i];
    }
  }
  return to.concat(ar || Array.prototype.slice.call(from));
}
function __makeTemplateObject(cooked, raw) {
  if (Object.defineProperty) {
    Object.defineProperty(cooked, "raw", {
      value: raw
    });
  } else {
    cooked.raw = raw;
  }
  return cooked;
}
var THEME_TOKENS = {
  avatar: {
    color: {
      backgroundDefault: '--avatar-color-background-default',
      background1: '--avatar-color-background1',
      background2: '--avatar-color-background2',
      background3: '--avatar-color-background3',
      background4: '--avatar-color-background4',
      border: '--avatar-color-border'
    }
  },
  base: {
    color: {
      /**
       * Color for primary text blocks (e.g. title, headings)
       */
      primaryText: '--base-color-primary-text',
      /**
       * Color for secondary text blocks (e.g. body text, caption)
       */
      secondaryText: '--base-color-secondary-text',
      /**
       * Color for text on primary actions (e.g. links, buttons)
       */
      action: '--base-color-action',
      /**
       * DEPRECATED, use action
       */
      link: '--base-color-link',
      /**
       * DEPRECATED, use secondaryText
       */
      bodyText: '--base-color-body-text',
      /**
       * DEPRECATED, use secondaryText
       */
      bodyTextSoft: '--base-color-body-text-soft',
      siteBackground: '--base-color-site-background',
      sidebarBackground: '--base-color-sidebar-background',
      /**
       * Used by the skeleton loader
       */
      loaderStart: '--base-color-loader-start',
      loaderEnd: '--base-color-loader-end',
      loadingBar: '--base-color-loading-bar',
      dim1: '--base-color-dim1',
      dim2: '--base-color-dim2',
      dim3: '--base-color-dim3',
      dim4: '--base-color-dim4',
      disabled: '--base-color-disabled',
      overlayBackdrop: '--base-color-overlay-backdrop',
      neutral0: '--base-color-neutral0',
      neutral50: '--base-color-neutral50',
      neutral100: '--base-color-neutral100',
      neutral200: '--base-color-neutral200',
      neutral300: '--base-color-neutral300',
      neutral400: '--base-color-neutral400',
      neutral500: '--base-color-neutral500',
      neutral600: '--base-color-neutral600',
      neutral700: '--base-color-neutral700',
      neutral800: '--base-color-neutral800',
      neutral900: '--base-color-neutral900',
      neutral950: '--base-color-neutral950',
      /**
       * Deprecated use neutral palette
       */
      white: '--base-color-white',
      /**
       * Deprecated use neutral palette
       */
      gray50: '--base-color-gray50',
      /**
       * Deprecated use neutral palette
       */
      gray100: '--base-color-gray100',
      /**
       * Deprecated use neutral palette
       */
      gray200: '--base-color-gray200',
      /**
       * Deprecated use neutral palette
       */
      gray300: '--base-color-gray300',
      /**
       * Deprecated use neutral palette
       */
      gray400: '--base-color-gray400',
      /**
       * Deprecated use neutral palette
       */
      gray500: '--base-color-gray500',
      /**
       * Deprecated use neutral palette
       */
      gray600: '--base-color-gray600',
      /**
       * Deprecated use neutral palette
       */
      gray700: '--base-color-gray700',
      /**
       * Deprecated use neutral palette
       */
      gray800: '--base-color-gray800',
      /**
       * Deprecated use neutral palette
       */
      gray900: '--base-color-gray900',
      /**
       * Deprecated use neutral palette
       */
      gray950: '--base-color-gray950',
      neutral: '--base-color-neutral',
      neutralBackground: '--base-color-neutral-background',
      neutralBorder: '--base-color-neutral-border',
      neutralText: '--base-color-neutral-text',
      success: '--base-color-success',
      successBackground: '--base-color-success-background',
      successBorder: '--base-color-success-border',
      successText: '--base-color-success-text',
      warning: '--base-color-warning',
      warningBackground: '--base-color-warning-background',
      warningBorder: '--base-color-warning-border',
      warningText: '--base-color-warning-text',
      danger: '--base-color-danger',
      dangerBackground: '--base-color-danger-background',
      dangerBorder: '--base-color-danger-border',
      dangerText: '--base-color-danger-text',
      info: '--base-color-info',
      infoBackground: '--base-color-info-background',
      infoBorder: '--base-color-info-border',
      infoText: '--base-color-info-text'
    },
    size: {
      rootText: '--base-size-root-text',
      bodyText: '--base-size-body-text',
      bodyProminentText: '--base-size-body-prominent-text',
      detailText: '--base-size-detail-text',
      header1: '--base-size-header1',
      header2: '--base-size-header2',
      header3: '--base-size-header3',
      header4: '--base-size-header4',
      borderRadius: '--base-size-border-radius',
      formInputText: '--base-size-form-input-text',
      titleCaseLetterSpacing: '--base-size-title-case-letter-spacing',
      titleCaseHeaderLetterSpacing: '--base-size-title-case-header-letter-spacing'
    },
    fontFamily: {
      body: '--base-font-family-body',
      heading: '--base-font-family-heading'
    },
    fontWeight: {
      normal: '--base-font-weight-normal',
      semiBold: '--base-font-weight-semi-bold',
      bold: '--base-font-weight-bold'
    },
    shadow: {
      raised: '--base-shadow-raised',
      floating: '--base-shadow-floating'
    }
  },
  badge: {
    color: {
      neutral: '--badge-color-neutral',
      neutralBackground: '--badge-color-neutral-background',
      neutralText: '--badge-color-neutral-text',
      success: '--badge-color-success',
      successBackground: '--badge-color-success-background',
      successText: '--badge-color-success-text',
      warning: '--badge-color-warning',
      warningBackground: '--badge-color-warning-background',
      warningText: '--badge-color-warning-text',
      danger: '--badge-color-danger',
      dangerBackground: '--badge-color-danger-background',
      dangerText: '--badge-color-danger-text',
      info: '--badge-color-info',
      infoBackground: '--badge-color-info-background',
      infoText: '--badge-color-info-text'
    },
    size: {
      text: '--badge-size-text',
      fontWeight: '--badge-size-font-weight',
      letterSpacing: '--badge-size-letter-spacing'
    }
  },
  button: {
    color: {
      disabledBackground: '--button-color-disabled',
      disabledText: '--button-color-disabled-text',
      disabledBorder: '--button-color-disabled-border'
    },
    size: {
      borderRadius: '--button-size-border-radius',
      border: '--button-size-border',
      small: '--button-size-small',
      form: '--button-size-form',
      medium: '--button-size-medium',
      large: '--button-size-large'
    }
  },
  card: {
    color: {
      border: '--card-color-border',
      background: '--card-color-background',
      backgroundSecondary: '--card-color-background-secondary'
    },
    size: {
      borderRadiusSmall: '--card-size-border-radius-small',
      borderRadiusMedium: '--card-size-border-radius-medium',
      borderRadiusLarge: '--card-size-border-radius-large'
    }
  },
  navigation: {
    color: {
      headingText: '--navigation-color-heading-text',
      link: '--navigation-color-link',
      linkSelected: '--navigation-color-link-selected',
      // the main background color of the navigation will eventually come from here
      // for now it is more efficient to have it load through legacy CSS
      background: '--navigation-color-background',
      sectionBackground: '--navigation-color-section-background',
      footerBackground: '--navigation-color-footer-background',
      helpCenterText: '--navigation-color-help-center-text',
      helpCenterBackground: '--navigation-color-help-center-background',
      hamburgerMobile: '--navigation-color-hamburger-mobile',
      // this is also referenced in AM5's legacy CSS
      border: '--navigation-color-border'
    }
  },
  notice: {
    color: {
      neutralBackground: '--notice-color-neutral-background',
      neutralBorder: '--notice-color-neutral-border',
      neutralText: '--notice-color-neutral-text',
      successBackground: '--notice-color-success-background',
      successBorder: '--notice-color-success-border',
      successText: '--notice-color-success-text',
      warningBackground: '--notice-color-warning-background',
      warningBorder: '--notice-color-warning-border',
      warningText: '--notice-color-warning-text',
      dangerBackground: '--notice-color-danger-background',
      dangerBorder: '--notice-color-danger-border',
      dangerText: '--notice-color-danger-text',
      infoBackground: '--notice-color-info-background',
      infoBorder: '--notice-color-info-border',
      infoText: '--notice-color-info-text'
    },
    size: {
      text: '--notice-size-text',
      fontWeight: '--notice-size-font-weight'
    }
  },
  flightOptions: {
    color: {
      airlineCardAirlineName: '--flight-options-color-airline-card-airline-name'
    }
  },
  textInput: {
    color: {
      border: '--text-input-color-border',
      disabled: '--text-input-color-disabled',
      disabledBorder: '--text-input-color-disabled-border',
      disabledText: '--text-input-color-disabled-text',
      background: '--text-input-color-background',
      backgroundHover: '--text-input-color-background-hover',
      placeholderText: '--text-input-color-placeholder-text'
    },
    shadow: {
      /** applied when input is focused */
      focus: '--text-input-shadow-focus'
    },
    size: {
      border: '--text-input-size-border',
      borderRadius: '--text-input-size-border-radius',
      text: '--text-input-size-text',
      minHeight: '--text-input-size-min-height'
    }
  }
};
var getTokenValue = function (token) {
  return "var(".concat(token, ")");
};
var SPACE = {
  size0: '0px',
  size2: '2px',
  size4: '4px',
  size6: '6px',
  size8: '8px',
  size12: '12px',
  size16: '16px',
  size24: '24px',
  size32: '32px',
  size48: '48px',
  size64: '64px',
  size96: '96px',
  size128: '128px'
};
var GENERIC_COLORS = {
  // White
  white: '#FFFFFF',
  blue50: '#EFF6FF',
  // background
  blue100: '#DBEAFE',
  blue200: '#BFDBFE',
  blue300: '#93C5FD',
  // border
  blue400: '#60A5FA',
  blue500: '#3B82F6',
  blue600: '#2563EB',
  blue700: '#1D4ED8',
  // info
  blue800: '#1E40AF',
  blue900: '#1A3276',
  // text
  blue950: '#172554',
  // Cyan
  cyan50: '#ECFEFF',
  cyan100: '#CFFAFE',
  cyan200: '#A5F3FC',
  cyan300: '#67E8F9',
  cyan400: '#22D3EE',
  cyan500: '#06B6D4',
  cyan600: '#0891B2',
  cyan700: '#0E7490',
  cyan800: '#155E75',
  cyan900: '#164E63',
  cyan950: '#083344',
  // Green
  green50: '#F0FDF4',
  // background
  green100: '#DCFCE7',
  green200: '#BBF7D0',
  green300: '#86EFAC',
  // border
  green400: '#4ADE80',
  green500: '#22C55E',
  // success
  green600: '#16A34A',
  green700: '#15803D',
  green800: '#166534',
  green900: '#14532D',
  // text
  green950: '#052E16',
  // gray
  gray50: '#fafafa',
  gray100: '#f7f7f7',
  // background
  gray200: '#f5f5f5',
  gray300: '#eeeeee',
  gray400: '#d6d6d6',
  gray500: '#c0c0c0',
  // neutral & border
  gray600: '#9e9e9e',
  gray700: '#757575',
  gray800: '#616161',
  gray900: '#424242',
  // text
  gray950: '#212121',
  // Orange
  orange50: '#FFFBEB',
  // background
  orange100: '#FEF3C7',
  orange200: '#FDE68A',
  orange300: '#FCD34D',
  // border
  orange400: '#FBBF24',
  orange500: '#F59E0B',
  orange600: '#D97706',
  // warning
  orange700: '#B45309',
  orange800: '#92400E',
  orange900: '#78350F',
  // text
  orange950: '#451A03',
  // Purple
  purple50: '#F5F3FF',
  purple100: '#EDE9FE',
  purple200: '#DDD6FE',
  purple300: '#D8B4FE',
  purple400: '#C084FC',
  purple500: '#A855F7',
  purple600: '#9333EA',
  purple700: '#7E22CE',
  purple800: '#6B21A8',
  purple900: '#581C87',
  purple950: '#3B0764',
  // Red
  red50: '#FEF2F2',
  // background
  red100: '#FEE2E2',
  red200: '#FECACA',
  red300: '#FCA5A5',
  // border
  red400: '#F87171',
  red500: '#EF4444',
  // danger
  red600: '#DC2626',
  red700: '#B91C1C',
  red800: '#991B1B',
  red900: '#7F1D1D',
  // text
  red950: '#450A0A'
};
var THEME_CONFIG = {
  base: {
    id: {
      logoInterstitial: 'config_base_id_logoInterstitial'
    },
    flag: {
      /**
       * indicated whether the theme is our flagship theme
       */
      isFlagshipSite: 'config_base_flag_isFlagshipSite'
    }
  },
  navigation: {
    flag: {
      showPoweredByAmtrav: 'config_navigation_flag_showPoweredByAmtrav'
    },
    id: {
      logo: 'config_navigation_id_logo',
      mode: 'config_navigation_id_mode'
    }
  }
};
var rootTheme = {
  themeSystemName: 'root',
  themeSystemConfigMap: new Map([[THEME_CONFIG.base.id.logoInterstitial, ''], [THEME_CONFIG.base.flag.isFlagshipSite, false], [THEME_CONFIG.navigation.flag.showPoweredByAmtrav, false], [THEME_CONFIG.navigation.id.logo, ''], [THEME_CONFIG.navigation.id.mode, 'dark']]),
  themeSystemCssPropertiesMap: new Map([
  // avatar
  [THEME_TOKENS.avatar.color.backgroundDefault, GENERIC_COLORS.gray300], [THEME_TOKENS.avatar.color.background1, GENERIC_COLORS.gray300], [THEME_TOKENS.avatar.color.background2, GENERIC_COLORS.gray300], [THEME_TOKENS.avatar.color.background3, GENERIC_COLORS.gray300], [THEME_TOKENS.avatar.color.background4, GENERIC_COLORS.gray300], [THEME_TOKENS.avatar.color.border, GENERIC_COLORS.gray500],
  // base
  [THEME_TOKENS.base.color.primaryText, GENERIC_COLORS.gray950], [THEME_TOKENS.base.color.secondaryText, GENERIC_COLORS.gray950], [THEME_TOKENS.base.color.action, GENERIC_COLORS.blue500], /* deprecated */[THEME_TOKENS.base.color.link, getTokenValue(THEME_TOKENS.base.color.action)], /* deprecated */[THEME_TOKENS.base.color.bodyText, getTokenValue(THEME_TOKENS.base.color.secondaryText)], /* deprecated */[THEME_TOKENS.base.color.bodyTextSoft, getTokenValue(THEME_TOKENS.base.color.secondaryText)], [THEME_TOKENS.base.color.siteBackground, GENERIC_COLORS.gray100], [THEME_TOKENS.base.color.sidebarBackground, 'transparent'], [THEME_TOKENS.base.color.loaderStart, '#d4dbe48c'], [THEME_TOKENS.base.color.loaderEnd, '#d4dbe4'], [THEME_TOKENS.base.color.loadingBar, THEME_TOKENS.base.color.action], [THEME_TOKENS.base.color.dim1, GENERIC_COLORS.gray100], [THEME_TOKENS.base.color.dim2, GENERIC_COLORS.gray300], [THEME_TOKENS.base.color.dim3, GENERIC_COLORS.gray400], [THEME_TOKENS.base.color.dim4, GENERIC_COLORS.gray500], [THEME_TOKENS.base.color.disabled, GENERIC_COLORS.gray200], [THEME_TOKENS.base.color.overlayBackdrop, 'rgba(25, 25, 15, 0.2)'], /* deprecated */[THEME_TOKENS.base.color.white, GENERIC_COLORS.white], /* deprecated */[THEME_TOKENS.base.color.gray50, GENERIC_COLORS.gray50], /* deprecated */[THEME_TOKENS.base.color.gray100, GENERIC_COLORS.gray100], /* deprecated */[THEME_TOKENS.base.color.gray200, GENERIC_COLORS.gray200], /* deprecated */[THEME_TOKENS.base.color.gray300, GENERIC_COLORS.gray300], /* deprecated */[THEME_TOKENS.base.color.gray400, GENERIC_COLORS.gray400], /* deprecated */[THEME_TOKENS.base.color.gray500, GENERIC_COLORS.gray500], /* deprecated */[THEME_TOKENS.base.color.gray600, GENERIC_COLORS.gray600], /* deprecated */[THEME_TOKENS.base.color.gray700, GENERIC_COLORS.gray700], /* deprecated */[THEME_TOKENS.base.color.gray800, GENERIC_COLORS.gray800], /* deprecated */[THEME_TOKENS.base.color.gray900, GENERIC_COLORS.gray900], /* deprecated */[THEME_TOKENS.base.color.gray950, GENERIC_COLORS.gray950], [THEME_TOKENS.base.color.neutral0, GENERIC_COLORS.white], [THEME_TOKENS.base.color.neutral50, GENERIC_COLORS.gray50], [THEME_TOKENS.base.color.neutral100, GENERIC_COLORS.gray100], [THEME_TOKENS.base.color.neutral200, GENERIC_COLORS.gray200], [THEME_TOKENS.base.color.neutral300, GENERIC_COLORS.gray300], [THEME_TOKENS.base.color.neutral400, GENERIC_COLORS.gray400], [THEME_TOKENS.base.color.neutral500, GENERIC_COLORS.gray500], [THEME_TOKENS.base.color.neutral600, GENERIC_COLORS.gray600], [THEME_TOKENS.base.color.neutral700, GENERIC_COLORS.gray700], [THEME_TOKENS.base.color.neutral800, GENERIC_COLORS.gray800], [THEME_TOKENS.base.color.neutral900, GENERIC_COLORS.gray900], [THEME_TOKENS.base.color.neutral950, GENERIC_COLORS.gray950],
  // neutral
  [THEME_TOKENS.base.color.neutral, GENERIC_COLORS.gray500], [THEME_TOKENS.base.color.neutralBackground, GENERIC_COLORS.gray50], [THEME_TOKENS.base.color.neutralBorder, GENERIC_COLORS.gray400], [THEME_TOKENS.base.color.neutralText, GENERIC_COLORS.gray900],
  // success
  [THEME_TOKENS.base.color.success, GENERIC_COLORS.green500], [THEME_TOKENS.base.color.successBackground, GENERIC_COLORS.green100], [THEME_TOKENS.base.color.successBorder, GENERIC_COLORS.green300], [THEME_TOKENS.base.color.successText, GENERIC_COLORS.green950],
  // warning
  [THEME_TOKENS.base.color.warning, GENERIC_COLORS.orange600], [THEME_TOKENS.base.color.warningBackground, GENERIC_COLORS.orange100], [THEME_TOKENS.base.color.warningBorder, GENERIC_COLORS.orange300], [THEME_TOKENS.base.color.warningText, GENERIC_COLORS.orange950],
  // danger
  [THEME_TOKENS.base.color.danger, GENERIC_COLORS.red500], [THEME_TOKENS.base.color.dangerBackground, GENERIC_COLORS.red50], [THEME_TOKENS.base.color.dangerBorder, GENERIC_COLORS.red300], [THEME_TOKENS.base.color.dangerText, GENERIC_COLORS.red950],
  // info
  [THEME_TOKENS.base.color.info, GENERIC_COLORS.blue700], [THEME_TOKENS.base.color.infoBackground, GENERIC_COLORS.blue50], [THEME_TOKENS.base.color.infoBorder, GENERIC_COLORS.blue300], [THEME_TOKENS.base.color.infoText, GENERIC_COLORS.blue950],
  // base.size
  [THEME_TOKENS.base.size.rootText, '14px'], [THEME_TOKENS.base.size.bodyText, getTokenValue(THEME_TOKENS.base.size.rootText)], [THEME_TOKENS.base.size.bodyProminentText, '16px'], [THEME_TOKENS.base.size.detailText, '12px'], [THEME_TOKENS.base.size.header1, '24px'], [THEME_TOKENS.base.size.header2, '22px'], [THEME_TOKENS.base.size.header3, '20px'], [THEME_TOKENS.base.size.header4, '16px'], [THEME_TOKENS.base.size.borderRadius, SPACE.size6], [THEME_TOKENS.base.size.formInputText, '14px'], [THEME_TOKENS.base.size.titleCaseLetterSpacing, '1.2px'], [THEME_TOKENS.base.size.titleCaseHeaderLetterSpacing, '1.83px'],
  // base.fontFamily
  [THEME_TOKENS.base.fontFamily.body, '"Source Sans Pro", system-ui, Arial, sans-serif'], [THEME_TOKENS.base.fontFamily.heading, getTokenValue(THEME_TOKENS.base.fontFamily.body)],
  // base.fontWeight
  [THEME_TOKENS.base.fontWeight.normal, '400'], [THEME_TOKENS.base.fontWeight.semiBold, '600'], [THEME_TOKENS.base.fontWeight.bold, '700'],
  // base.shadow
  [THEME_TOKENS.base.shadow.raised, '0px 3px 12px 0px rgba(0, 0, 0, 0.08), 0px 1px 6px 0px rgba(0, 0, 0, 0.08), 0px 0px 2px 0px rgba(0, 0, 0, 0.12)'], [THEME_TOKENS.base.shadow.floating, '0px 1px 1px 0px rgba(0, 0, 0, 0.05), 0px 6px 8px 0px rgba(0, 0, 0, 0.06), 0px 3px 3px 0px rgba(0, 0, 0, 0.035)'],
  // badge.color
  // neutral
  [THEME_TOKENS.badge.color.neutral, getTokenValue(THEME_TOKENS.base.color.neutral)], [THEME_TOKENS.badge.color.neutralBackground, GENERIC_COLORS.gray200], [THEME_TOKENS.badge.color.neutralText, GENERIC_COLORS.gray800],
  // success
  [THEME_TOKENS.badge.color.success, getTokenValue(THEME_TOKENS.base.color.success)], [THEME_TOKENS.badge.color.successBackground, getTokenValue(THEME_TOKENS.base.color.successBackground)], [THEME_TOKENS.badge.color.successText, getTokenValue(THEME_TOKENS.base.color.successText)],
  // warning
  [THEME_TOKENS.badge.color.warning, getTokenValue(THEME_TOKENS.base.color.warning)], [THEME_TOKENS.badge.color.warningBackground, getTokenValue(THEME_TOKENS.base.color.warningBackground)], [THEME_TOKENS.badge.color.warningText, getTokenValue(THEME_TOKENS.base.color.warningText)],
  // danger
  [THEME_TOKENS.badge.color.danger, getTokenValue(THEME_TOKENS.base.color.danger)], [THEME_TOKENS.badge.color.dangerBackground, GENERIC_COLORS.red100], [THEME_TOKENS.badge.color.dangerText, getTokenValue(THEME_TOKENS.base.color.dangerText)],
  // info
  [THEME_TOKENS.badge.color.info, getTokenValue(THEME_TOKENS.base.color.info)], [THEME_TOKENS.badge.color.infoBackground, getTokenValue(THEME_TOKENS.base.color.infoBackground)], [THEME_TOKENS.badge.color.infoText, getTokenValue(THEME_TOKENS.base.color.infoText)],
  // badge.size
  [THEME_TOKENS.badge.size.text, '14px'], [THEME_TOKENS.badge.size.fontWeight, getTokenValue(THEME_TOKENS.base.fontWeight.semiBold)], [THEME_TOKENS.badge.size.letterSpacing, '0.8px'],
  // button.color
  [THEME_TOKENS.button.color.disabledBackground, getTokenValue(THEME_TOKENS.base.color.disabled)], [THEME_TOKENS.button.color.disabledBorder, getTokenValue(THEME_TOKENS.base.color.disabled)], [THEME_TOKENS.button.color.disabledText, GENERIC_COLORS.gray600],
  // button.size
  [THEME_TOKENS.button.size.borderRadius, SPACE.size4], [THEME_TOKENS.button.size.border, '1px'], [THEME_TOKENS.button.size.small, '14px'], [THEME_TOKENS.button.size.form, getTokenValue(THEME_TOKENS.base.size.formInputText)], [THEME_TOKENS.button.size.medium, SPACE.size16], [THEME_TOKENS.button.size.large, SPACE.size24],
  // card.color
  [THEME_TOKENS.card.color.border, getTokenValue(THEME_TOKENS.base.color.dim3)], [THEME_TOKENS.card.color.background, getTokenValue(THEME_TOKENS.base.color.dim1)], [THEME_TOKENS.card.color.backgroundSecondary, getTokenValue(THEME_TOKENS.base.color.dim2)],
  // card.size
  [THEME_TOKENS.card.size.borderRadiusSmall, SPACE.size4], [THEME_TOKENS.card.size.borderRadiusMedium, SPACE.size8], [THEME_TOKENS.card.size.borderRadiusLarge, SPACE.size12],
  // navigation.color
  [THEME_TOKENS.navigation.color.headingText, GENERIC_COLORS.blue300], [THEME_TOKENS.navigation.color.link, getTokenValue(THEME_TOKENS.base.color.neutral0)], [THEME_TOKENS.navigation.color.linkSelected, getTokenValue(THEME_TOKENS.base.color.neutral0)],
  // the main background color of the navigation will eventually come from here
  // for now it is more efficient to have it load through legacy CSS
  [THEME_TOKENS.navigation.color.background, getTokenValue(THEME_TOKENS.base.color.neutral950)], [THEME_TOKENS.navigation.color.sectionBackground, 'rgba(0, 0, 0, 0.085)'], [THEME_TOKENS.navigation.color.footerBackground, 'rgba(0, 0, 0, 0.4)'], [THEME_TOKENS.navigation.color.helpCenterText, getTokenValue(THEME_TOKENS.base.color.neutral0)], [THEME_TOKENS.navigation.color.helpCenterBackground, 'rgba(255, 255, 255, 0.3)'], [THEME_TOKENS.navigation.color.hamburgerMobile, getTokenValue(THEME_TOKENS.base.color.neutral0)], [THEME_TOKENS.navigation.color.border, 'rgba(130, 130, 130, 0.35)'],
  // notice.color
  // neutral
  [THEME_TOKENS.notice.color.neutralBackground, getTokenValue(THEME_TOKENS.base.color.neutralBackground)], [THEME_TOKENS.notice.color.neutralBorder, getTokenValue(THEME_TOKENS.base.color.neutralBorder)], [THEME_TOKENS.notice.color.neutralText, getTokenValue(THEME_TOKENS.base.color.neutralText)],
  // success
  [THEME_TOKENS.notice.color.successBackground, getTokenValue(THEME_TOKENS.base.color.successBackground)], [THEME_TOKENS.notice.color.successBorder, getTokenValue(THEME_TOKENS.base.color.successBorder)], [THEME_TOKENS.notice.color.successText, getTokenValue(THEME_TOKENS.base.color.successText)],
  // warning
  [THEME_TOKENS.notice.color.warningBackground, getTokenValue(THEME_TOKENS.base.color.warningBackground)], [THEME_TOKENS.notice.color.warningBorder, getTokenValue(THEME_TOKENS.base.color.warningBorder)], [THEME_TOKENS.notice.color.warningText, getTokenValue(THEME_TOKENS.base.color.warningText)],
  // danger
  [THEME_TOKENS.notice.color.dangerBackground, getTokenValue(THEME_TOKENS.base.color.dangerBackground)], [THEME_TOKENS.notice.color.dangerBorder, getTokenValue(THEME_TOKENS.base.color.dangerBorder)], [THEME_TOKENS.notice.color.dangerText, getTokenValue(THEME_TOKENS.base.color.dangerText)],
  // info
  [THEME_TOKENS.notice.color.infoBackground, getTokenValue(THEME_TOKENS.base.color.infoBackground)], [THEME_TOKENS.notice.color.infoBorder, getTokenValue(THEME_TOKENS.base.color.infoBorder)], [THEME_TOKENS.notice.color.infoText, getTokenValue(THEME_TOKENS.base.color.infoText)],
  // notice.size
  [THEME_TOKENS.notice.size.text, '16px'], [THEME_TOKENS.notice.size.fontWeight, getTokenValue(THEME_TOKENS.base.fontWeight.semiBold)],
  // flightOptions.color
  [THEME_TOKENS.flightOptions.color.airlineCardAirlineName, getTokenValue(THEME_TOKENS.base.color.action)],
  // textInput.color
  [THEME_TOKENS.textInput.color.border, getTokenValue(THEME_TOKENS.base.color.dim3)], [THEME_TOKENS.textInput.color.disabled, getTokenValue(THEME_TOKENS.base.color.disabled)], [THEME_TOKENS.textInput.color.disabledBorder, getTokenValue(THEME_TOKENS.base.color.disabled)], [THEME_TOKENS.textInput.color.disabledText, GENERIC_COLORS.gray600], [THEME_TOKENS.textInput.color.background, getTokenValue(THEME_TOKENS.base.color.neutral0)], [THEME_TOKENS.textInput.color.backgroundHover, getTokenValue(THEME_TOKENS.base.color.dim1)], [THEME_TOKENS.textInput.color.placeholderText, GENERIC_COLORS.gray600],
  // textInput.shadow
  [THEME_TOKENS.textInput.shadow.focus, 'none'],
  // textInput.size
  [THEME_TOKENS.textInput.size.border, '1px'], [THEME_TOKENS.textInput.size.borderRadius, SPACE.size4], [THEME_TOKENS.textInput.size.text, getTokenValue(THEME_TOKENS.base.size.formInputText)], [THEME_TOKENS.textInput.size.minHeight, '38px']])
};
var BOUNCE_NEXT_COLORS = {
  blue50: '#f1f7fd',
  blue75: '#E7F1FD',
  blue100: '#dcebfc',
  blue200: '#cfe0f8',
  blue300: '#a1ccfd',
  blue400: '#59a3fd',
  /**
   * primary color
   */
  blue500: '#0776fc',
  blue600: '#1054c1',
  blue700: '#073e97',
  blue800: '#07347e',
  blue900: '#132b6c',
  blue950: '#15203d',
  // Cyan
  cyan50: '#f2fcfd',
  cyan100: '#d8f9fd',
  cyan200: '#c5f6fc',
  cyan300: '#a6edf6',
  cyan400: '#87e5f0',
  cyan500: '#60cfdc',
  cyan600: '#1fb9ca',
  cyan700: '#0fa5b6',
  cyan800: '#0c8592',
  cyan900: '#0f5961',
  cyan950: '#0b4147',
  // Green
  green50: '#e8f8f2',
  green100: '#b3f0da',
  green200: '#a0e5cc',
  green300: '#62daaf',
  green400: '#00cd83',
  green500: '#05b777',
  green600: '#039761',
  green700: '#067e53',
  green800: '#036d47',
  green900: '#005234',
  green950: '#003d27',
  // gray
  gray50: '#fafafa',
  gray100: '#f7f7f7',
  gray200: '#f5f5f5',
  gray300: '#eeeeee',
  gray400: '#d6d6d6',
  gray500: '#c0c0c0',
  gray600: '#9e9e9e',
  gray700: '#757575',
  gray800: '#616161',
  gray900: '#424242',
  /**
   * Also used as a text color
   */
  gray950: '#212121',
  // Orange
  orange50: '#fffaf2',
  orange100: '#fcefdf',
  orange200: '#f8ddb9',
  orange300: '#f9d09a',
  orange400: '#fbc774',
  orange500: '#fcb727',
  orange600: '#e68e16',
  orange700: '#be730e',
  orange800: '#905504',
  orange900: '#5c3909',
  orange950: '#3a2303',
  // Purple
  purple50: '#f3f4fd',
  purple100: '#e1e0fa',
  purple200: '#d0cff7',
  purple300: '#c1c0f7',
  purple400: '#b2a8f5',
  purple500: '#a089dc',
  purple600: '#9673e7',
  purple700: '#7b4dcc',
  purple800: '#6631af',
  purple900: '#502a7e',
  purple950: '#3c1d63',
  // Red
  red50: '#fdeded',
  red100: '#f9cdcd',
  red200: '#f49f9f',
  red300: '#ef7676',
  red400: '#eb5757',
  red500: '#e73636',
  red600: '#d61919',
  red700: '#b21515',
  red800: '#8d1111',
  red900: '#640c0c',
  red950: '#490909'
};
var bounceNextTheme = {
  themeSystemName: 'bouncenext',
  themeSystemConfigMap: new Map(__spreadArray(__spreadArray([], __read(rootTheme.themeSystemConfigMap), false), [[THEME_CONFIG.base.id.logoInterstitial, 'BounceNextInterstitial'], [THEME_CONFIG.navigation.id.logo, 'BounceNextNavigation']], false)),
  themeSystemCssPropertiesMap: new Map(__spreadArray(__spreadArray([], __read(rootTheme.themeSystemCssPropertiesMap), false), [
  // base
  [THEME_TOKENS.base.color.primaryText, BOUNCE_NEXT_COLORS.blue950], [THEME_TOKENS.base.color.action, BOUNCE_NEXT_COLORS.blue500], [THEME_TOKENS.base.color.loadingBar, BOUNCE_NEXT_COLORS.orange500], [THEME_TOKENS.base.color.dim1, BOUNCE_NEXT_COLORS.blue50], [THEME_TOKENS.base.color.dim2, BOUNCE_NEXT_COLORS.blue100], [THEME_TOKENS.base.color.dim3, BOUNCE_NEXT_COLORS.blue200], [THEME_TOKENS.base.color.dim4, BOUNCE_NEXT_COLORS.blue300], [THEME_TOKENS.base.color.success, BOUNCE_NEXT_COLORS.green400], [THEME_TOKENS.base.color.successBackground, BOUNCE_NEXT_COLORS.green50], [THEME_TOKENS.base.color.successBorder, BOUNCE_NEXT_COLORS.green100], [THEME_TOKENS.base.color.successText, BOUNCE_NEXT_COLORS.green900], [THEME_TOKENS.base.color.warning, BOUNCE_NEXT_COLORS.orange600], [THEME_TOKENS.base.color.warningBackground, BOUNCE_NEXT_COLORS.orange100], [THEME_TOKENS.base.color.warningBorder, BOUNCE_NEXT_COLORS.orange400], [THEME_TOKENS.base.color.warningText, BOUNCE_NEXT_COLORS.orange900], [THEME_TOKENS.base.color.danger, BOUNCE_NEXT_COLORS.red500], [THEME_TOKENS.base.color.dangerBackground, BOUNCE_NEXT_COLORS.red50], [THEME_TOKENS.base.color.dangerBorder, BOUNCE_NEXT_COLORS.red200], [THEME_TOKENS.base.color.dangerText, BOUNCE_NEXT_COLORS.red900], [THEME_TOKENS.base.color.info, BOUNCE_NEXT_COLORS.blue700], [THEME_TOKENS.base.color.infoBackground, BOUNCE_NEXT_COLORS.blue50], [THEME_TOKENS.base.color.infoBorder, BOUNCE_NEXT_COLORS.blue200], [THEME_TOKENS.base.color.infoText, BOUNCE_NEXT_COLORS.blue900], [THEME_TOKENS.base.fontFamily.body, '"Public Sans", Helvetica, Arial, sans-serif'],
  // size
  [THEME_TOKENS.base.size.borderRadius, SPACE.size0],
  // badge
  [THEME_TOKENS.badge.color.warningBackground, BOUNCE_NEXT_COLORS.orange100],
  // button
  [THEME_TOKENS.button.size.borderRadius, SPACE.size32], [THEME_TOKENS.button.size.border, SPACE.size2],
  // textInput
  [THEME_TOKENS.textInput.size.border, SPACE.size2], [THEME_TOKENS.textInput.size.borderRadius, '0']], false))
};
({
  themeSystemName: 'a2b',
  themeSystemConfigMap: new Map(__spreadArray(__spreadArray([], __read(bounceNextTheme.themeSystemConfigMap), false), [[THEME_CONFIG.base.id.logoInterstitial, 'A2bInterstitial'], [THEME_CONFIG.navigation.id.logo, 'A2bNavigation']], false)),
  themeSystemCssPropertiesMap: new Map(__spreadArray([], __read(bounceNextTheme.themeSystemCssPropertiesMap), false))
});
({
  themeSystemName: 'atom',
  themeSystemConfigMap: new Map(__spreadArray(__spreadArray([], __read(bounceNextTheme.themeSystemConfigMap), false), [[THEME_CONFIG.base.id.logoInterstitial, 'ATOMInterstitial'], [THEME_CONFIG.navigation.id.logo, 'ATOMNavigation']], false)),
  themeSystemCssPropertiesMap: new Map(__spreadArray(__spreadArray([], __read(bounceNextTheme.themeSystemCssPropertiesMap), false), [
  // base
  [THEME_TOKENS.base.fontFamily.body, 'Roboto, Helvetica, Arial, sans-serif'], [THEME_TOKENS.base.fontWeight.semiBold, '500'], [THEME_TOKENS.base.color.primaryText, BOUNCE_NEXT_COLORS.gray950], [THEME_TOKENS.base.color.secondaryText, BOUNCE_NEXT_COLORS.gray800], [THEME_TOKENS.base.color.infoBorder, BOUNCE_NEXT_COLORS.gray400], [THEME_TOKENS.base.color.success, BOUNCE_NEXT_COLORS.green600], [THEME_TOKENS.base.color.successBorder, BOUNCE_NEXT_COLORS.green300],
  // size
  [THEME_TOKENS.base.size.borderRadius, SPACE.size6], [THEME_TOKENS.base.size.formInputText, '14px'], [THEME_TOKENS.base.size.header3, '18px'],
  // button
  [THEME_TOKENS.button.size.borderRadius, SPACE.size4], [THEME_TOKENS.button.size.small, SPACE.size12], [THEME_TOKENS.button.size.border, '1px'],
  // card
  [THEME_TOKENS.card.color.border, BOUNCE_NEXT_COLORS.gray400],
  // textInput
  [THEME_TOKENS.textInput.size.border, '1px'], [THEME_TOKENS.textInput.color.border, BOUNCE_NEXT_COLORS.gray500], [THEME_TOKENS.textInput.size.borderRadius, SPACE.size4], [THEME_TOKENS.textInput.size.minHeight, '26px'], [THEME_TOKENS.textInput.color.backgroundHover, BOUNCE_NEXT_COLORS.blue75]], false))
});
var ADVISOR_CONSOLE_COLORS = {
  lightBlueMist: '#eff9ff',
  // card
  lightBlue: '#9bd1e4',
  cgBlue: '#007da5',
  darkBlue: '#165d85',
  cyan: '#21a9cd',
  amazonite: '#12c5b2',
  mantisGreen: '#75cb44',
  cherry: '#db3260',
  libertyPurple: '#6353a3',
  backgroundCream: '#fffeed',
  // textfield
  grainYellow: '#F7D45E',
  // NOTICE
  success: '#4da129',
  successBackground: '#edf6ea',
  successBorder: '#bbdbae',
  successText: '#204411',
  warning: '#e68e16',
  warningBackground: '#fdf4e8',
  warningBorder: '#f6d4a6',
  warningText: '#613c09',
  danger: '#a12929',
  dangerBackground: '#f6eaea',
  dangerBorder: '#dbaeae',
  dangerText: '#441111',
  blue: '#156da8',
  // info
  infoBackground: '#e8f0f6',
  infoBorder: '#a6c8de',
  infoText: '#092e47',
  // BADGE
  successBackgroundBadge: '#defbd0',
  warningBackgroundBadge: '#ffecd2'
};
({
  themeSystemName: 'advisorconsole',
  themeSystemConfigMap: new Map(__spreadArray(__spreadArray([], __read(rootTheme.themeSystemConfigMap), false), [[THEME_CONFIG.base.id.logoInterstitial, '']], false)),
  themeSystemCssPropertiesMap: new Map(__spreadArray(__spreadArray([], __read(rootTheme.themeSystemCssPropertiesMap), false), [[THEME_TOKENS.base.color.primaryText, ADVISOR_CONSOLE_COLORS.darkBlue], [THEME_TOKENS.base.color.action, ADVISOR_CONSOLE_COLORS.cyan], [THEME_TOKENS.base.color.dim1, ADVISOR_CONSOLE_COLORS.lightBlueMist], [THEME_TOKENS.base.color.success, ADVISOR_CONSOLE_COLORS.success], [THEME_TOKENS.base.color.successBackground, ADVISOR_CONSOLE_COLORS.successBackground], [THEME_TOKENS.base.color.successBorder, ADVISOR_CONSOLE_COLORS.successBorder], [THEME_TOKENS.base.color.successText, ADVISOR_CONSOLE_COLORS.successText], [THEME_TOKENS.base.color.warning, ADVISOR_CONSOLE_COLORS.warning], [THEME_TOKENS.base.color.warningBackground, ADVISOR_CONSOLE_COLORS.warningBackground], [THEME_TOKENS.base.color.warningBorder, ADVISOR_CONSOLE_COLORS.warningBorder], [THEME_TOKENS.base.color.warningText, ADVISOR_CONSOLE_COLORS.warningText], [THEME_TOKENS.base.color.danger, ADVISOR_CONSOLE_COLORS.danger], [THEME_TOKENS.base.color.dangerBackground, ADVISOR_CONSOLE_COLORS.dangerBackground], [THEME_TOKENS.base.color.dangerBorder, ADVISOR_CONSOLE_COLORS.dangerBorder], [THEME_TOKENS.base.color.dangerText, ADVISOR_CONSOLE_COLORS.dangerText], [THEME_TOKENS.base.color.info, ADVISOR_CONSOLE_COLORS.blue], [THEME_TOKENS.base.color.infoBackground, ADVISOR_CONSOLE_COLORS.infoBackground], [THEME_TOKENS.base.color.infoBorder, ADVISOR_CONSOLE_COLORS.infoBorder], [THEME_TOKENS.base.color.infoText, ADVISOR_CONSOLE_COLORS.infoText],
  // badge.color
  [THEME_TOKENS.badge.color.successBackground, ADVISOR_CONSOLE_COLORS.successBackgroundBadge], [THEME_TOKENS.badge.color.warningBackground, ADVISOR_CONSOLE_COLORS.warningBackgroundBadge]], false))
});
var ABACUS_COLORS = {
  lightGrey: '#f5f5f5',
  lightColor: '#f4f9fd',
  blueJeans: '#12a9fb',
  // nav
  medBlue: '#048CD6',
  darkBlue: '#2C3F5E',
  skyPop: '#76CBF9',
  // nav
  // BADGE / NOTICE
  infoDark: '#424243',
  infoBackground: '#f2f2f2',
  infoBorder: '#b7b7b8',
  infoText: '#1c1c1c'
};
({
  themeSystemName: 'abacus',
  themeSystemConfigMap: new Map(__spreadArray(__spreadArray([], __read(rootTheme.themeSystemConfigMap), false), [[THEME_CONFIG.base.id.logoInterstitial, 'AbacusInterstitial'], [THEME_CONFIG.navigation.id.logo, 'AbacusNavigation'], [THEME_CONFIG.navigation.flag.showPoweredByAmtrav, true]], false)),
  themeSystemCssPropertiesMap: new Map(__spreadArray(__spreadArray([], __read(rootTheme.themeSystemCssPropertiesMap), false), [[THEME_TOKENS.base.color.primaryText, ABACUS_COLORS.darkBlue], [THEME_TOKENS.base.color.secondaryText, ABACUS_COLORS.darkBlue], [THEME_TOKENS.base.color.action, ABACUS_COLORS.medBlue], [THEME_TOKENS.base.color.loadingBar, ABACUS_COLORS.medBlue], [THEME_TOKENS.base.color.dim1, ABACUS_COLORS.lightColor],
  // success, warning, danger are inherited from root
  [THEME_TOKENS.base.color.info, ABACUS_COLORS.infoDark], [THEME_TOKENS.base.color.infoBackground, ABACUS_COLORS.infoBackground], [THEME_TOKENS.base.color.infoBorder, ABACUS_COLORS.infoBorder], [THEME_TOKENS.base.color.infoText, ABACUS_COLORS.infoText],
  // navigation
  [THEME_TOKENS.navigation.color.headingText, ABACUS_COLORS.skyPop], [THEME_TOKENS.navigation.color.background, ABACUS_COLORS.darkBlue]], false))
});
var CAVA_COLORS = {
  gray100: 'rgba(25, 25, 15, 0.05)',
  gray300: 'rgba(25, 25, 15, 0.1)',
  // neutral
  neutral0: '#FFFFFF',
  neutral100: '#FAFAF5',
  neutral200: '#F5F5EB',
  // off white
  neutral500: '#D2D2C8',
  neutral600: '#919183',
  neutral700: '#838377',
  neutral800: '#6E6E64',
  neutral900: '#44443C',
  neutral950: '#14140F',
  // off black
  // Red
  red100: '#FDE7ED',
  red600: '#D20F46',
  // Orange
  orange500: '#FF8C5F',
  // amber
  amber100: '#FFEDB8',
  amber400: '#FFBE00',
  amber700: '#805F00',
  amber900: '#5C4400',
  // lime´
  lime400: '#BEFF50',
  // perk
  // green
  green200: '#D6FFEF',
  green800: '#007D4B',
  // emerald
  emerald50: '#EDFFFD',
  emerald800: '#06806E',
  // cyan
  cyan400: '#82DCFA',
  // blue
  blue50: '#F0F5FF',
  blue900: '#002971',
  // purple
  purple50: '#F1F0FF',
  purple300: '#C8A0FF',
  purple700: '#5647B2',
  // pink
  pink300: '#FAB9FA'
};
({
  themeSystemName: 'cava',
  themeSystemConfigMap: new Map(__spreadArray(__spreadArray([], __read(rootTheme.themeSystemConfigMap), false), [[THEME_CONFIG.base.flag.isFlagshipSite, true], [THEME_CONFIG.base.id.logoInterstitial, 'CavaInterstitial'], [THEME_CONFIG.navigation.id.logo, 'CavaNavigation']], false)),
  themeSystemCssPropertiesMap: new Map(__spreadArray(__spreadArray([], __read(rootTheme.themeSystemCssPropertiesMap), false), [
  // avatar
  [THEME_TOKENS.avatar.color.background1, CAVA_COLORS.orange500], [THEME_TOKENS.avatar.color.background2, CAVA_COLORS.cyan400], [THEME_TOKENS.avatar.color.background3, CAVA_COLORS.pink300], [THEME_TOKENS.avatar.color.background4, CAVA_COLORS.purple300], [THEME_TOKENS.avatar.color.border, 'transparent'],
  // base
  [THEME_TOKENS.base.color.primaryText, CAVA_COLORS.neutral950], [THEME_TOKENS.base.color.secondaryText, CAVA_COLORS.neutral800], [THEME_TOKENS.base.color.action, CAVA_COLORS.neutral950], [THEME_TOKENS.base.color.siteBackground, CAVA_COLORS.neutral0], [THEME_TOKENS.base.color.sidebarBackground, 'transparent'], [THEME_TOKENS.base.color.loaderStart, CAVA_COLORS.gray100], [THEME_TOKENS.base.color.loaderEnd, CAVA_COLORS.gray300], [THEME_TOKENS.base.color.loadingBar, CAVA_COLORS.lime400], [THEME_TOKENS.base.color.dim1, CAVA_COLORS.neutral100],
  // neutral is inherited from root
  // success
  [THEME_TOKENS.base.color.success, CAVA_COLORS.green800], [THEME_TOKENS.base.color.successBackground, CAVA_COLORS.green200], [THEME_TOKENS.base.color.successBorder, CAVA_COLORS.green800], [THEME_TOKENS.base.color.successText, CAVA_COLORS.green800],
  // warning & warningBackground are inherited from root
  [THEME_TOKENS.base.color.warningBorder, getTokenValue(THEME_TOKENS.base.color.warning)], [THEME_TOKENS.base.color.warningText, getTokenValue(THEME_TOKENS.base.color.warning)],
  // danger
  [THEME_TOKENS.base.color.danger, CAVA_COLORS.red600], [THEME_TOKENS.base.color.dangerBackground, CAVA_COLORS.red100], [THEME_TOKENS.base.color.dangerBorder, CAVA_COLORS.red600], [THEME_TOKENS.base.color.dangerText, CAVA_COLORS.red600],
  // info
  [THEME_TOKENS.base.color.info, CAVA_COLORS.neutral500], [THEME_TOKENS.base.color.infoBackground, CAVA_COLORS.neutral100], [THEME_TOKENS.base.color.infoBorder, CAVA_COLORS.neutral500], [THEME_TOKENS.base.color.infoText, CAVA_COLORS.neutral800], [THEME_TOKENS.base.size.rootText, '16px'], [THEME_TOKENS.base.size.header1, '33px'], [THEME_TOKENS.base.size.header2, '23px'], [THEME_TOKENS.base.size.header3, '19px'], [THEME_TOKENS.base.size.header4, '16px'], [THEME_TOKENS.base.size.formInputText, '16px'], [THEME_TOKENS.base.fontFamily.body, '"PerkSans", Arial, sans-serif'], [THEME_TOKENS.base.fontFamily.heading, getTokenValue(THEME_TOKENS.base.fontFamily.body)], [THEME_TOKENS.base.fontWeight.bold, '600'],
  // badge
  // info
  [THEME_TOKENS.badge.color.infoBackground, CAVA_COLORS.neutral200], [THEME_TOKENS.badge.color.infoText, CAVA_COLORS.neutral800],
  // warning
  [THEME_TOKENS.badge.color.warningText, CAVA_COLORS.amber700],
  // button
  [THEME_TOKENS.button.color.disabledText, CAVA_COLORS.neutral700],
  // card
  [THEME_TOKENS.card.color.border, CAVA_COLORS.neutral500], [THEME_TOKENS.card.color.background, CAVA_COLORS.neutral100], [THEME_TOKENS.card.color.backgroundSecondary, CAVA_COLORS.neutral200],
  // notice
  [THEME_TOKENS.notice.color.successBackground, getTokenValue(THEME_TOKENS.base.color.successBackground)], [THEME_TOKENS.notice.color.successBorder, getTokenValue(THEME_TOKENS.base.color.successBorder)], [THEME_TOKENS.notice.color.successText, getTokenValue(THEME_TOKENS.base.color.primaryText)], [THEME_TOKENS.notice.color.warningBackground, getTokenValue(THEME_TOKENS.base.color.warningBackground)], [THEME_TOKENS.notice.color.warningBorder, getTokenValue(THEME_TOKENS.base.color.warningBorder)], [THEME_TOKENS.notice.color.warningText, getTokenValue(THEME_TOKENS.base.color.primaryText)], [THEME_TOKENS.notice.color.dangerBackground, getTokenValue(THEME_TOKENS.base.color.dangerBackground)], [THEME_TOKENS.notice.color.dangerBorder, getTokenValue(THEME_TOKENS.base.color.dangerBorder)], [THEME_TOKENS.notice.color.dangerText, getTokenValue(THEME_TOKENS.base.color.primaryText)], [THEME_TOKENS.notice.color.infoBackground, getTokenValue(THEME_TOKENS.base.color.infoBackground)], [THEME_TOKENS.notice.color.infoBorder, getTokenValue(THEME_TOKENS.base.color.infoBorder)], [THEME_TOKENS.notice.color.infoText, getTokenValue(THEME_TOKENS.base.color.primaryText)], [THEME_TOKENS.notice.size.fontWeight, getTokenValue(THEME_TOKENS.base.fontWeight.normal)],
  // flightOptions
  [THEME_TOKENS.flightOptions.color.airlineCardAirlineName, getTokenValue(THEME_TOKENS.base.color.secondaryText)],
  // textInput.color
  [THEME_TOKENS.textInput.color.border, CAVA_COLORS.neutral600], [THEME_TOKENS.textInput.color.backgroundHover, CAVA_COLORS.gray100], [THEME_TOKENS.textInput.color.placeholderText, getTokenValue(THEME_TOKENS.base.color.secondaryText)], [THEME_TOKENS.textInput.color.disabledText, CAVA_COLORS.neutral700],
  // textInput.shadow
  [THEME_TOKENS.textInput.shadow.focus, "0px 0px 0px 1px ".concat(getTokenValue(THEME_TOKENS.base.color.action))],
  // textInput.size
  [THEME_TOKENS.textInput.size.borderRadius, SPACE.size8],
  // navigation
  [THEME_TOKENS.navigation.color.headingText, getTokenValue(THEME_TOKENS.base.color.primaryText)], [THEME_TOKENS.navigation.color.link, CAVA_COLORS.neutral800], [THEME_TOKENS.navigation.color.linkSelected, getTokenValue(THEME_TOKENS.base.color.primaryText)], [THEME_TOKENS.navigation.color.background, CAVA_COLORS.neutral100], [THEME_TOKENS.navigation.color.sectionBackground, CAVA_COLORS.neutral200], [THEME_TOKENS.navigation.color.footerBackground, CAVA_COLORS.neutral0], [THEME_TOKENS.navigation.color.helpCenterBackground, CAVA_COLORS.neutral950], [THEME_TOKENS.navigation.color.hamburgerMobile, CAVA_COLORS.neutral950], [THEME_TOKENS.navigation.color.border, CAVA_COLORS.neutral500]], false))
});
var spendColorsObj = {
  // INFO (modified with tinycolor)
  info1: '#0D74CE',
  // passport11
  info2: '#e7f1fa',
  info3: '#a3caec',
  info4: '#053157',
  // CORE (colors for multiple Emburse themes)
  checkpoint11_2: '#a12226',
  checkpoint11: '#CE2C31',
  checkpoint12: '#641723',
  checkpoint3: '#FEEBEC',
  checkpoint5: '#FFCDCE',
  departure11_2: '#1a6645',
  departure11: '#218358',
  departure12: '#193B2D',
  departure3: '#E6F6EB',
  departure5: '#C4E8D1',
  passport11_2: '#0A5AA1',
  passport11: '#0D74CE',
  passport12: '#113264',
  passport3: '#E6F4FE',
  passport5: '#C2E5FF',
  runway0: '#FFFFFF',
  runway1: '#FCFCFD',
  runway11: '#60646C',
  runway12: '#1C2024',
  runway2: '#F9F9FB',
  runway4: '#E8E8EC',
  runway6: '#D9D9E0',
  runway8: '#B9BBC6',
  winsock10: '#EF5F00',
  winsock12: '#582D1D',
  winsock3: '#FFEFD6',
  winsock5: '#FFD19A'
};
var EMBURSE_COLORS = __assign({
  // PAGE
  pagePrimary: spendColorsObj.runway1,
  pageSecondary: spendColorsObj.runway2,
  pageWhite: spendColorsObj.runway0,
  // SURFACE
  surfacePrimary: spendColorsObj.runway0,
  surfacePrimaryHover: spendColorsObj.passport3,
  surfacePrimarySelected: spendColorsObj.passport5,
  surfaceAccent: spendColorsObj.passport3,
  surfaceWarning: spendColorsObj.winsock3,
  surfaceError: spendColorsObj.checkpoint3,
  surfaceSuccess: spendColorsObj.departure3,
  surfaceNeutral: spendColorsObj.runway4,
  // BORDER
  borderPrimary: spendColorsObj.runway4,
  borderSecondary: spendColorsObj.runway6,
  borderAccent: spendColorsObj.passport5,
  borderWarning: spendColorsObj.winsock5,
  borderError: spendColorsObj.checkpoint5,
  borderSuccess: spendColorsObj.departure5,
  // TEXT
  textPrimary: spendColorsObj.runway12,
  textSecondary: spendColorsObj.runway11,
  textLink: spendColorsObj.passport11,
  textLinkHover: spendColorsObj.passport11_2,
  textError: spendColorsObj.checkpoint11,
  textDisabled: spendColorsObj.runway8,
  textReverse: spendColorsObj.runway0,
  // NOTIFICATION
  notificationDefault: spendColorsObj.runway4,
  notificationAccent: spendColorsObj.passport11,
  notificationWarning: spendColorsObj.winsock10,
  notificationUrgent: spendColorsObj.checkpoint11,
  notificationConfirm: spendColorsObj.departure11,
  // BUTTON
  buttonPrimary: spendColorsObj.passport11,
  buttonPrimaryHover: spendColorsObj.passport11_2,
  buttonPrimaryActive: spendColorsObj.passport12,
  buttonPrimaryText: spendColorsObj.runway0,
  buttonSecondary: spendColorsObj.runway0,
  buttonSecondaryText: spendColorsObj.passport11,
  buttonSecondaryStroke: spendColorsObj.passport11,
  buttonSecondaryHover: spendColorsObj.passport3,
  buttonSecondaryActive: spendColorsObj.passport5,
  buttonDisabled: spendColorsObj.runway6,
  // BUTTON EXTRA
  buttonExtraDanger: spendColorsObj.checkpoint11,
  buttonExtraDangerHover: spendColorsObj.checkpoint11_2,
  buttonExtraDangerActive: spendColorsObj.checkpoint12,
  buttonExtraSuccess: spendColorsObj.departure11,
  buttonExtraSuccessHover: spendColorsObj.departure11_2,
  buttonExtraSuccessActive: spendColorsObj.departure12,
  // ICON
  iconRegular: spendColorsObj.runway12,
  iconDisabled: spendColorsObj.runway8,
  iconAction: spendColorsObj.passport11,
  iconWarning: spendColorsObj.winsock10,
  iconError: spendColorsObj.checkpoint11,
  iconSuccess: spendColorsObj.departure11,
  iconReverse: spendColorsObj.runway0,
  // ALERT
  alertAccent: spendColorsObj.passport3,
  alertAccentText: spendColorsObj.passport12,
  alertWarning: spendColorsObj.winsock3,
  alertWarningText: spendColorsObj.winsock12,
  alertError: spendColorsObj.checkpoint3,
  alertErrorText: spendColorsObj.checkpoint12,
  alertSuccess: spendColorsObj.departure3,
  alertSuccessText: spendColorsObj.departure12
}, spendColorsObj);
var emburseTheme = {
  themeSystemName: 'emburse',
  themeSystemConfigMap: new Map(__spreadArray(__spreadArray([], __read(rootTheme.themeSystemConfigMap), false), [[THEME_CONFIG.navigation.flag.showPoweredByAmtrav, true]], false)),
  themeSystemCssPropertiesMap: new Map(__spreadArray(__spreadArray([], __read(rootTheme.themeSystemCssPropertiesMap), false), [[THEME_TOKENS.base.color.primaryText, EMBURSE_COLORS.textPrimary], [THEME_TOKENS.base.color.secondaryText, EMBURSE_COLORS.textSecondary], [THEME_TOKENS.base.color.action, EMBURSE_COLORS.passport11], [THEME_TOKENS.base.color.siteBackground, EMBURSE_COLORS.pagePrimary], [THEME_TOKENS.base.color.loadingBar, EMBURSE_COLORS.passport11],
  // dim1 is inherited from root
  [THEME_TOKENS.base.color.dim2, EMBURSE_COLORS.surfaceAccent], [THEME_TOKENS.base.color.disabled, EMBURSE_COLORS.textDisabled], [THEME_TOKENS.base.color.success, EMBURSE_COLORS.notificationConfirm], [THEME_TOKENS.base.color.successBackground, EMBURSE_COLORS.alertSuccess], [THEME_TOKENS.base.color.successBorder, EMBURSE_COLORS.borderSuccess], [THEME_TOKENS.base.color.successText, EMBURSE_COLORS.alertSuccessText], [THEME_TOKENS.base.color.warning, EMBURSE_COLORS.notificationWarning], [THEME_TOKENS.base.color.warningBackground, EMBURSE_COLORS.alertWarning], [THEME_TOKENS.base.color.warningBorder, EMBURSE_COLORS.borderWarning], [THEME_TOKENS.base.color.warningText, EMBURSE_COLORS.alertWarningText], [THEME_TOKENS.base.color.danger, EMBURSE_COLORS.notificationUrgent], [THEME_TOKENS.base.color.dangerBackground, EMBURSE_COLORS.alertError], [THEME_TOKENS.base.color.dangerBorder, EMBURSE_COLORS.borderError], [THEME_TOKENS.base.color.dangerText, EMBURSE_COLORS.alertErrorText], [THEME_TOKENS.base.color.info, EMBURSE_COLORS.notificationAccent], [THEME_TOKENS.base.color.infoBackground, EMBURSE_COLORS.alertAccent], [THEME_TOKENS.base.color.infoBorder, EMBURSE_COLORS.borderAccent], [THEME_TOKENS.base.color.infoText, EMBURSE_COLORS.alertAccentText], [THEME_TOKENS.card.color.border, EMBURSE_COLORS.borderPrimary],
  // navigation
  [THEME_TOKENS.navigation.color.headingText, EMBURSE_COLORS.info3], [THEME_TOKENS.navigation.color.background, EMBURSE_COLORS.passport12]], false))
};
({
  themeSystemName: 'certify',
  themeSystemConfigMap: new Map(__spreadArray(__spreadArray([], __read(rootTheme.themeSystemConfigMap), false), [[THEME_CONFIG.base.id.logoInterstitial, 'CertifyInterstitial'], [THEME_CONFIG.navigation.id.logo, 'CertifyNavigation'], [THEME_CONFIG.navigation.flag.showPoweredByAmtrav, true]], false)),
  themeSystemCssPropertiesMap: new Map(__spreadArray([], __read(emburseTheme.themeSystemCssPropertiesMap), false))
});
({
  themeSystemName: 'chromeriver',
  themeSystemConfigMap: new Map(__spreadArray(__spreadArray([], __read(rootTheme.themeSystemConfigMap), false), [[THEME_CONFIG.base.id.logoInterstitial, 'ChromeRiverInterstitial'], [THEME_CONFIG.navigation.id.logo, 'ChromeRiverNavigation'], [THEME_CONFIG.navigation.flag.showPoweredByAmtrav, true]], false)),
  themeSystemCssPropertiesMap: new Map(__spreadArray([], __read(emburseTheme.themeSystemCssPropertiesMap), false))
});
var EA_COLORS = {
  lightBlue: '#95c4ff',
  // nav
  sapphireBlue: '#0E51A8',
  polynesianBlue: '#0c4388',
  pistachioGreen: '#8dc577',
  // nav
  // NOTICE
  asparagusGreen: '#5fa444',
  // success
  successBackground: '#eff6ec',
  successBorder: '#c2dcb8',
  successText: '#28451d',
  orange: '#e87e04',
  // warning
  warningBackground: '#fdf2e6',
  warningBorder: '#f6cea0',
  warningText: '#613502',
  red: '#D64541',
  // danger
  dangerBackground: '#fbecec',
  dangerBorder: '#efb8b7',
  dangerText: '#5a1d1b',
  infoBlue: '#156da8',
  // nav
  infoBackground: '#e8f0f6',
  infoBorder: '#a6c8de',
  infoText: '#092e47',
  // BADGE
  successBackgroundBadge: '#e1f7d9',
  warningBackgroundBadge: '#ffecd5'
};
({
  themeSystemName: 'ea',
  themeSystemConfigMap: new Map(__spreadArray(__spreadArray([], __read(rootTheme.themeSystemConfigMap), false), [[THEME_CONFIG.base.id.logoInterstitial, 'EAInterstitial'], [THEME_CONFIG.navigation.id.logo, 'EANavigation'], [THEME_CONFIG.navigation.flag.showPoweredByAmtrav, true]], false)),
  themeSystemCssPropertiesMap: new Map(__spreadArray(__spreadArray([], __read(rootTheme.themeSystemCssPropertiesMap), false), [[THEME_TOKENS.base.color.primaryText, EA_COLORS.polynesianBlue], [THEME_TOKENS.base.color.action, EA_COLORS.sapphireBlue], [THEME_TOKENS.base.color.loadingBar, EA_COLORS.sapphireBlue],
  // dim colors are inherited from root
  [THEME_TOKENS.base.color.success, EA_COLORS.asparagusGreen], [THEME_TOKENS.base.color.successBackground, EA_COLORS.successBackground], [THEME_TOKENS.base.color.successBorder, EA_COLORS.successBorder], [THEME_TOKENS.base.color.successText, EA_COLORS.successText], [THEME_TOKENS.base.color.warning, EA_COLORS.orange], [THEME_TOKENS.base.color.warningBackground, EA_COLORS.warningBackground], [THEME_TOKENS.base.color.warningBorder, EA_COLORS.warningBorder], [THEME_TOKENS.base.color.warningText, EA_COLORS.warningText], [THEME_TOKENS.base.color.danger, EA_COLORS.red], [THEME_TOKENS.base.color.dangerBackground, EA_COLORS.dangerBackground], [THEME_TOKENS.base.color.dangerBorder, EA_COLORS.dangerBorder], [THEME_TOKENS.base.color.dangerText, EA_COLORS.dangerText], [THEME_TOKENS.base.color.info, EA_COLORS.infoBlue], [THEME_TOKENS.base.color.infoBackground, EA_COLORS.infoBackground], [THEME_TOKENS.base.color.infoBorder, EA_COLORS.infoBorder], [THEME_TOKENS.base.color.infoText, EA_COLORS.infoText],
  // badge.color
  [THEME_TOKENS.badge.color.successBackground, EA_COLORS.successBackgroundBadge], [THEME_TOKENS.badge.color.warningBackground, EA_COLORS.warningBackgroundBadge],
  // navigation
  [THEME_TOKENS.navigation.color.background, EA_COLORS.sapphireBlue], [THEME_TOKENS.navigation.color.border, EA_COLORS.infoBlue]], false))
});
var EDENRED_PAY_COLORS = {
  white: '#FFFFFF',
  lightGrey: '#e9edf3',
  // nav
  grey: '#6D7787',
  black: '#000000',
  // nav
  coldWhite: '#F1F7FF',
  accentCobalt: '#dff4f9',
  lightCobalt: '#B0EFFF',
  // nav
  cobalt: '#0D8AFF',
  // nav
  darkBlue: '#22222d',
  // nav
  red: '#F72717',
  // nav
  violet: '#711CFF',
  mint: '#00A184',
  // NOTICE
  olive: '#81C105',
  // success
  successBackground: '#f2f9e6',
  successBorder: '#cfe7a0',
  successText: '#365102',
  orange: '#f79517',
  // warning
  warningBackground: '#fef4e8',
  warningBorder: '#fcd7a7',
  warningText: '#683f0a',
  vibrantRed: '#FF007D',
  // danger
  dangerBackground: '#ffe6f2',
  dangerBorder: '#ff9ece',
  dangerText: '#6b0035',
  infoCobalt: '#00529f',
  infoBackground: '#e6eef5',
  infoBorder: '#9ebddb',
  infoText: '#002243',
  // BADGE
  successBackgroundBadge: '#e8f6ce',
  warningBackgroundBadge: '#ffefda'
};
({
  themeSystemName: 'edenredpay',
  themeSystemConfigMap: new Map(__spreadArray(__spreadArray([], __read(rootTheme.themeSystemConfigMap), false), [[THEME_CONFIG.base.id.logoInterstitial, 'EdenredPayInterstitial'], [THEME_CONFIG.navigation.id.logo, 'EdenredPayNavigation'],
  // edenredpay is the only white label theme that has a light navigation background color
  // which does not work with the current "powered by AmTrav" logo with white text
  [THEME_CONFIG.navigation.flag.showPoweredByAmtrav, false]], false)),
  themeSystemCssPropertiesMap: new Map(__spreadArray(__spreadArray([], __read(rootTheme.themeSystemCssPropertiesMap), false), [[THEME_TOKENS.base.color.primaryText, EDENRED_PAY_COLORS.black], [THEME_TOKENS.base.color.secondaryText, EDENRED_PAY_COLORS.black], [THEME_TOKENS.base.color.action, EDENRED_PAY_COLORS.cobalt], [THEME_TOKENS.base.color.loadingBar, EDENRED_PAY_COLORS.cobalt], [THEME_TOKENS.base.color.dim1, EDENRED_PAY_COLORS.coldWhite], [THEME_TOKENS.base.color.dim2, EDENRED_PAY_COLORS.accentCobalt], [THEME_TOKENS.base.color.success, EDENRED_PAY_COLORS.olive], [THEME_TOKENS.base.color.successBackground, EDENRED_PAY_COLORS.successBackground], [THEME_TOKENS.base.color.successBorder, EDENRED_PAY_COLORS.successBorder], [THEME_TOKENS.base.color.successText, EDENRED_PAY_COLORS.successText], [THEME_TOKENS.base.color.warning, EDENRED_PAY_COLORS.orange], [THEME_TOKENS.base.color.warningBackground, EDENRED_PAY_COLORS.warningBackground], [THEME_TOKENS.base.color.warningBorder, EDENRED_PAY_COLORS.warningBorder], [THEME_TOKENS.base.color.warningText, EDENRED_PAY_COLORS.warningText], [THEME_TOKENS.base.color.danger, EDENRED_PAY_COLORS.vibrantRed], [THEME_TOKENS.base.color.dangerBackground, EDENRED_PAY_COLORS.dangerBackground], [THEME_TOKENS.base.color.dangerBorder, EDENRED_PAY_COLORS.dangerBorder], [THEME_TOKENS.base.color.dangerText, EDENRED_PAY_COLORS.dangerText], [THEME_TOKENS.base.color.info, EDENRED_PAY_COLORS.infoCobalt], [THEME_TOKENS.base.color.infoBackground, EDENRED_PAY_COLORS.infoBackground], [THEME_TOKENS.base.color.infoBorder, EDENRED_PAY_COLORS.infoBorder], [THEME_TOKENS.base.color.infoText, EDENRED_PAY_COLORS.infoText],
  // badge.color
  [THEME_TOKENS.badge.color.successBackground, EDENRED_PAY_COLORS.successBackgroundBadge], [THEME_TOKENS.badge.color.warningBackground, EDENRED_PAY_COLORS.warningBackgroundBadge],
  // navigation
  [THEME_TOKENS.navigation.color.headingText, EDENRED_PAY_COLORS.red], [THEME_TOKENS.navigation.color.link, EDENRED_PAY_COLORS.black], [THEME_TOKENS.navigation.color.background, EDENRED_PAY_COLORS.white], [THEME_TOKENS.navigation.color.linkSelected, EDENRED_PAY_COLORS.black], [THEME_TOKENS.navigation.color.sectionBackground, EDENRED_PAY_COLORS.coldWhite], [THEME_TOKENS.navigation.color.border, EDENRED_PAY_COLORS.lightGrey]], false))
});
var LASSO_COLORS = {
  black: '#000000',
  darkGrey: '#262626',
  // nav
  blue: '#008aab',
  yellow: '#f5bf3b',
  // nav
  goldenSand: '#EDD391',
  // nav
  charcoalNavBorder: '#414141',
  // nav
  // NOTICE
  green: '#5fa444',
  // success
  successBackground: '#eff6ec',
  successBorder: '#c2dcb8',
  successText: '#28451d',
  orange: '#e87e04',
  // warning
  warningBackground: '#fdf2e6',
  warningBorder: '#f6cea0',
  warningText: '#613502',
  red: '#D64541',
  // danger
  dangerBackground: '#fbecec',
  dangerBorder: '#efb8b7',
  dangerText: '#5a1d1b',
  infoBlue: '#156da8',
  infoBackground: '#e8f0f6',
  infoBorder: '#a6c8de',
  infoText: '#092e47',
  // BADGE
  successBackgroundBadge: '#e1f7d9',
  warningBackgroundBadge: '#ffecd5'
};
({
  themeSystemName: 'lasso',
  themeSystemConfigMap: new Map(__spreadArray(__spreadArray([], __read(rootTheme.themeSystemConfigMap), false), [[THEME_CONFIG.base.id.logoInterstitial, 'LassoInterstitial'], [THEME_CONFIG.navigation.id.logo, 'LassoNavigation'], [THEME_CONFIG.navigation.flag.showPoweredByAmtrav, true]], false)),
  themeSystemCssPropertiesMap: new Map(__spreadArray(__spreadArray([], __read(rootTheme.themeSystemCssPropertiesMap), false), [[THEME_TOKENS.base.color.primaryText, LASSO_COLORS.black], [THEME_TOKENS.base.color.action, LASSO_COLORS.blue],
  // dim colors are inherited from root
  [THEME_TOKENS.base.color.success, LASSO_COLORS.green], [THEME_TOKENS.base.color.successBackground, LASSO_COLORS.successBackground], [THEME_TOKENS.base.color.successBorder, LASSO_COLORS.successBorder], [THEME_TOKENS.base.color.successText, LASSO_COLORS.successText], [THEME_TOKENS.base.color.warning, LASSO_COLORS.orange], [THEME_TOKENS.base.color.warningBackground, LASSO_COLORS.warningBackground], [THEME_TOKENS.base.color.warningBorder, LASSO_COLORS.warningBorder], [THEME_TOKENS.base.color.warningText, LASSO_COLORS.warningText], [THEME_TOKENS.base.color.danger, LASSO_COLORS.red], [THEME_TOKENS.base.color.dangerBackground, LASSO_COLORS.dangerBackground], [THEME_TOKENS.base.color.dangerBorder, LASSO_COLORS.dangerBorder], [THEME_TOKENS.base.color.dangerText, LASSO_COLORS.dangerText], [THEME_TOKENS.base.color.info, LASSO_COLORS.infoBlue], [THEME_TOKENS.base.color.infoBackground, LASSO_COLORS.infoBackground], [THEME_TOKENS.base.color.infoBorder, LASSO_COLORS.infoBorder], [THEME_TOKENS.base.color.infoText, LASSO_COLORS.infoText],
  // badge.color
  [THEME_TOKENS.badge.color.successBackground, LASSO_COLORS.successBackgroundBadge], [THEME_TOKENS.badge.color.warningBackground, LASSO_COLORS.warningBackgroundBadge],
  // navigation
  [THEME_TOKENS.navigation.color.headingText, LASSO_COLORS.goldenSand], [THEME_TOKENS.navigation.color.background, LASSO_COLORS.black], [THEME_TOKENS.navigation.color.border, LASSO_COLORS.charcoalNavBorder]], false))
});

// CERTIFY - CHROME RIVER - EMBURSE
var NEXONIA_COLORS = {
  // link
  azureBlue: '#2681DD',
  yaleBlue: '#154E88',
  // active
  // navigation
  sapphireBlue: '#005EB8',
  blueNavBorder: '#4a84bb',
  // nav
  // subhead
  carolinaBlue: '#0097DC',
  // secondary
  culturedWhite: '#F3F4F5',
  platinum: '#E5E7E9',
  // hover
  gainsboro: '#D7DBDE',
  // active
  // primary
  lightGreen: '#6fe593',
  // nav
  pantoneGreen: '#1FB64D',
  forestGreenWeb: '#188A3B',
  // hover
  cadmiumGreen: '#105F28',
  // active
  // NOTICE
  malachiteGreen: '#23D158',
  // success
  successBackground: '#e9faee',
  successBorder: '#abeec0',
  successText: '#0f5825',
  greenBlue: '#1C68B4',
  // hover & info
  infoBackground: '#e8f0f8',
  infoBorder: '#a9c6e3',
  infoText: '#0c2c4c',
  // BADGE
  successBackgroundBadge: '#def6e5'
};
({
  themeSystemName: 'nexonia',
  themeSystemConfigMap: new Map(__spreadArray(__spreadArray([], __read(rootTheme.themeSystemConfigMap), false), [[THEME_CONFIG.base.id.logoInterstitial, 'NexoniaInterstitial'], [THEME_CONFIG.navigation.id.logo, 'NexoniaNavigation'], [THEME_CONFIG.navigation.flag.showPoweredByAmtrav, true]], false)),
  themeSystemCssPropertiesMap: new Map(__spreadArray(__spreadArray([], __read(rootTheme.themeSystemCssPropertiesMap), false), [
  // primaryText & secondaryText are inherited from root
  [THEME_TOKENS.base.color.action, NEXONIA_COLORS.azureBlue], [THEME_TOKENS.base.color.loadingBar, NEXONIA_COLORS.pantoneGreen], [THEME_TOKENS.base.color.dim1, NEXONIA_COLORS.culturedWhite],
  // warning, danger are inherited from root
  [THEME_TOKENS.base.color.success, NEXONIA_COLORS.malachiteGreen], [THEME_TOKENS.base.color.successBackground, NEXONIA_COLORS.successBackground], [THEME_TOKENS.base.color.successBorder, NEXONIA_COLORS.successBorder], [THEME_TOKENS.base.color.successText, NEXONIA_COLORS.successText], [THEME_TOKENS.base.color.info, NEXONIA_COLORS.greenBlue], [THEME_TOKENS.base.color.infoBackground, NEXONIA_COLORS.infoBackground], [THEME_TOKENS.base.color.infoBorder, NEXONIA_COLORS.infoBorder], [THEME_TOKENS.base.color.infoText, NEXONIA_COLORS.infoText],
  // badge
  [THEME_TOKENS.badge.color.successBackground, NEXONIA_COLORS.successBackgroundBadge],
  // navigation
  [THEME_TOKENS.navigation.color.background, NEXONIA_COLORS.sapphireBlue], [THEME_TOKENS.navigation.color.border, NEXONIA_COLORS.blueNavBorder]], false))
});
({
  themeSystemName: 'spend',
  themeSystemConfigMap: new Map(__spreadArray(__spreadArray([], __read(rootTheme.themeSystemConfigMap), false), [[THEME_CONFIG.base.id.logoInterstitial, 'SpendInterstitial'], [THEME_CONFIG.navigation.id.logo, 'SpendNavigation'], [THEME_CONFIG.navigation.flag.showPoweredByAmtrav, true]], false)),
  themeSystemCssPropertiesMap: new Map(__spreadArray([], __read(emburseTheme.themeSystemCssPropertiesMap), false))
});
var SUTISOFT_COLORS = {
  lightBlueJeans: '#41b0f7',
  // nav
  blue: '#0081c0',
  darkBlue: '#00496f',
  softAzure: '#82C1E9',
  // nav
  blueNavBorder: '#236283',
  // nav
  // NOTICE
  green: '#5fa444',
  // success
  successBackground: '#eff6ec',
  successBorder: '#c2dcb8',
  successText: '#28451d',
  orange: '#e87e04',
  // warning
  warningBackground: '#fdf2e6',
  warningBorder: '#f6cea0',
  warningText: '#613502',
  red: '#D64541',
  // danger
  dangerBackground: '#fbecec',
  dangerBorder: '#efb8b7',
  dangerText: '#5a1d1b',
  infoBlue: '#156da8',
  infoBackground: '#e8f0f6',
  infoBorder: '#a6c8de',
  infoText: '#092e47',
  // BADGE
  successBackgroundBadge: '#e1f7d9',
  warningBackgroundBadge: '#ffecd5'
};
({
  themeSystemName: 'sutisoft',
  themeSystemConfigMap: new Map(__spreadArray(__spreadArray([], __read(rootTheme.themeSystemConfigMap), false), [[THEME_CONFIG.base.id.logoInterstitial, 'SutiSoftInterstitial'], [THEME_CONFIG.navigation.id.logo, 'SutiSoftNavigation'], [THEME_CONFIG.navigation.flag.showPoweredByAmtrav, true]], false)),
  themeSystemCssPropertiesMap: new Map(__spreadArray(__spreadArray([], __read(rootTheme.themeSystemCssPropertiesMap), false), [[THEME_TOKENS.base.color.primaryText, SUTISOFT_COLORS.darkBlue], [THEME_TOKENS.base.color.action, SUTISOFT_COLORS.blue], [THEME_TOKENS.base.color.loadingBar, SUTISOFT_COLORS.blue],
  // dim colors are inherited from root
  [THEME_TOKENS.base.color.success, SUTISOFT_COLORS.green], [THEME_TOKENS.base.color.successBackground, SUTISOFT_COLORS.successBackground], [THEME_TOKENS.base.color.successBorder, SUTISOFT_COLORS.successBorder], [THEME_TOKENS.base.color.successText, SUTISOFT_COLORS.successText], [THEME_TOKENS.base.color.warning, SUTISOFT_COLORS.orange], [THEME_TOKENS.base.color.warningBackground, SUTISOFT_COLORS.warningBackground], [THEME_TOKENS.base.color.warningBorder, SUTISOFT_COLORS.warningBorder], [THEME_TOKENS.base.color.warningText, SUTISOFT_COLORS.warningText], [THEME_TOKENS.base.color.danger, SUTISOFT_COLORS.red], [THEME_TOKENS.base.color.dangerBackground, SUTISOFT_COLORS.dangerBackground], [THEME_TOKENS.base.color.dangerBorder, SUTISOFT_COLORS.dangerBorder], [THEME_TOKENS.base.color.dangerText, SUTISOFT_COLORS.dangerText], [THEME_TOKENS.base.color.info, SUTISOFT_COLORS.infoBlue], [THEME_TOKENS.base.color.infoBackground, SUTISOFT_COLORS.infoBackground], [THEME_TOKENS.base.color.infoBorder, SUTISOFT_COLORS.infoBorder], [THEME_TOKENS.base.color.infoText, SUTISOFT_COLORS.infoText],
  // badge.color
  [THEME_TOKENS.badge.color.successBackground, SUTISOFT_COLORS.successBackgroundBadge], [THEME_TOKENS.badge.color.warningBackground, SUTISOFT_COLORS.warningBackgroundBadge],
  // navigation
  [THEME_TOKENS.navigation.color.headingText, SUTISOFT_COLORS.softAzure], [THEME_TOKENS.navigation.color.background, SUTISOFT_COLORS.darkBlue], [THEME_TOKENS.navigation.color.border, SUTISOFT_COLORS.blueNavBorder]], false))
});
var SVB_COLORS = {
  bkgdGrey: '#F0F0F0',
  lightGrey: '#DCDCDC',
  lightBlueMist: '#f7fdff',
  // table
  cyanProcess: '#3ebded',
  lightBlue: '#33BBEF',
  // chat
  darkBlue: '#007DB8',
  prussianBlue: '#033149',
  crystalSurf: '#8FE0FF',
  // nav
  blueNavBorder: '#236283',
  // nav
  // NOTICE
  success: '#4BAF46',
  successBackground: '#edf7ed',
  successBorder: '#bbe1b9',
  successText: '#204a1d',
  warning: '#E77503',
  warningBackground: '#fdf1e6',
  warningBorder: '#f6cb9f',
  warningText: '#613101',
  danger: '#B00020',
  dangerBackground: '#f7e6e9',
  dangerBorder: '#e19eaa',
  dangerText: '#4a000d',
  deepBlue: '#004B6C',
  // info
  infoBackground: '#e6edf0',
  infoBorder: '#9ebbc7',
  infoText: '#00202d',
  // BADGE
  successBackgroundBadge: '#dffadf',
  warningBackgroundBadge: '#ffeddb'
};
({
  themeSystemName: 'svb',
  themeSystemConfigMap: new Map(__spreadArray(__spreadArray([], __read(rootTheme.themeSystemConfigMap), false), [[THEME_CONFIG.base.id.logoInterstitial, 'SVBInterstitial'], [THEME_CONFIG.navigation.id.logo, 'SVBNavigation'], [THEME_CONFIG.navigation.flag.showPoweredByAmtrav, true]], false)),
  themeSystemCssPropertiesMap: new Map(__spreadArray(__spreadArray([], __read(rootTheme.themeSystemCssPropertiesMap), false), [
  // primaryText & secondaryText are inherited from root
  [THEME_TOKENS.base.color.action, SVB_COLORS.cyanProcess], [THEME_TOKENS.base.color.loadingBar, SVB_COLORS.darkBlue], [THEME_TOKENS.base.color.dim1, SVB_COLORS.lightBlueMist], [THEME_TOKENS.base.color.success, SVB_COLORS.success], [THEME_TOKENS.base.color.successBackground, SVB_COLORS.successBackground], [THEME_TOKENS.base.color.successBorder, SVB_COLORS.successBorder], [THEME_TOKENS.base.color.successText, SVB_COLORS.successText], [THEME_TOKENS.base.color.warning, SVB_COLORS.warning], [THEME_TOKENS.base.color.warningBackground, SVB_COLORS.warningBackground], [THEME_TOKENS.base.color.warningBorder, SVB_COLORS.warningBorder], [THEME_TOKENS.base.color.warningText, SVB_COLORS.warningText], [THEME_TOKENS.base.color.danger, SVB_COLORS.danger], [THEME_TOKENS.base.color.dangerBackground, SVB_COLORS.dangerBackground], [THEME_TOKENS.base.color.dangerBorder, SVB_COLORS.dangerBorder], [THEME_TOKENS.base.color.dangerText, SVB_COLORS.dangerText], [THEME_TOKENS.base.color.info, SVB_COLORS.deepBlue], [THEME_TOKENS.base.color.infoBackground, SVB_COLORS.infoBackground], [THEME_TOKENS.base.color.infoBorder, SVB_COLORS.infoBorder], [THEME_TOKENS.base.color.infoText, SVB_COLORS.infoText], [THEME_TOKENS.badge.color.successBackground, SVB_COLORS.successBackgroundBadge], [THEME_TOKENS.badge.color.warningBackground, SVB_COLORS.warningBackgroundBadge],
  // navigation
  [THEME_TOKENS.navigation.color.headingText, SVB_COLORS.crystalSurf], [THEME_TOKENS.navigation.color.background, SVB_COLORS.deepBlue], [THEME_TOKENS.navigation.color.border, SVB_COLORS.blueNavBorder]], false))
});

// CERTIFY - CHROME RIVER - EMBURSE
var TALLIE_COLORS = {
  lightGrey: '#f5f5f5',
  lightColor: '#f5f9f1',
  // nav
  lightGreenTitle: '#ceff71',
  // nav
  lightGreen: '#7cbe00',
  green: '#6fa60f',
  darkGreen: '#406009',
  // BADGE / NOTICE
  infoDark: '#424241',
  infoBackground: '#f2f2f2',
  infoBorder: '#b7b7b7',
  infoText: '#1c1c1b'
};
({
  themeSystemName: 'tallie',
  themeSystemConfigMap: new Map(__spreadArray(__spreadArray([], __read(rootTheme.themeSystemConfigMap), false), [[THEME_CONFIG.base.id.logoInterstitial, 'TallieInterstitial'], [THEME_CONFIG.navigation.id.logo, 'TallieNavigation'], [THEME_CONFIG.navigation.flag.showPoweredByAmtrav, true]], false)),
  themeSystemCssPropertiesMap: new Map(__spreadArray(__spreadArray([], __read(rootTheme.themeSystemCssPropertiesMap), false), [
  // primaryText & secondaryText is inherited from root
  [THEME_TOKENS.base.color.action, TALLIE_COLORS.green], [THEME_TOKENS.base.color.loadingBar, TALLIE_COLORS.green], [THEME_TOKENS.base.color.dim1, TALLIE_COLORS.lightColor],
  // success, warning, danger are inherited from root
  [THEME_TOKENS.base.color.info, TALLIE_COLORS.infoDark], [THEME_TOKENS.base.color.infoBackground, TALLIE_COLORS.infoBackground], [THEME_TOKENS.base.color.infoBorder, TALLIE_COLORS.infoBorder], [THEME_TOKENS.base.color.infoText, TALLIE_COLORS.infoText],
  // navigation
  [THEME_TOKENS.navigation.color.headingText, TALLIE_COLORS.lightGreenTitle], [THEME_TOKENS.navigation.color.background, TALLIE_COLORS.lightGreen]], false))
});
function generateCustomProperties(theme) {
  var styles = '';
  var themeSystemCssPropertiesMap = theme.themeSystemCssPropertiesMap;
  if (!themeSystemCssPropertiesMap) return styles;
  themeSystemCssPropertiesMap.forEach(function (value, key) {
    styles += "".concat(key, ": ").concat(value, ";");
  });
  return styles;
}
createGlobalStyle(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n\n  // CSS reset\n  // https://www.joshwcomeau.com/css/custom-css-reset/\n  *,\n  *::before,\n  *::after {\n    box-sizing: border-box;\n  }\n\n  * {\n    margin: 0;\n  }\n\n  html {\n    -webkit-tap-highlight-color: rgba(0, 0, 0, 0);\n  }\n\n  body {\n    font-family: ", ";\n    font-size: ", ";\n    color: ", ";\n    -webkit-font-smoothing: antialiased;\n    -webkit-text-size-adjust: none;\n  }\n\n  p {\n    line-height: calc(1em + 0.5rem);\n    padding: 0;\n  }\n\n  b,\n  strong {\n    font-weight: bold;\n  }\n\n  a {\n    color: ", ";\n    text-decoration: underline;\n\n    &:hover,\n    &:focus,\n    &:active {\n      color: ", ";\n      text-decoration: none;\n    }\n\n    &:hover {\n      cursor: pointer;\n    }\n\n    &:focus {\n      outline: 0;\n    }\n  }\n\n  // Custom scrollbar\n\n\n  //media selector for mouse input devices\n  @media (pointer: fine) {\n    ::-webkit-scrollbar {\n        width: 12px;\n        height: 12px;\n    }\n\n    ::-webkit-scrollbar-track {\n        -webkit-box-shadow: inset 0 0 12px rgb(214, 214, 214);\n    }\n\n    ::-webkit-scrollbar-thumb {\n        background-color: #b5b5b5;\n        border: 2px solid #e7e7e7;\n        border-radius: 12px;\n    }\n\n    ::-webkit-scrollbar-thumb:hover {\n        background-color: #999999;\n    }\n  }\n"], ["\n\n  // CSS reset\n  // https://www.joshwcomeau.com/css/custom-css-reset/\n  *,\n  *::before,\n  *::after {\n    box-sizing: border-box;\n  }\n\n  * {\n    margin: 0;\n  }\n\n  html {\n    -webkit-tap-highlight-color: rgba(0, 0, 0, 0);\n  }\n\n  body {\n    font-family: ", ";\n    font-size: ", ";\n    color: ", ";\n    -webkit-font-smoothing: antialiased;\n    -webkit-text-size-adjust: none;\n  }\n\n  p {\n    line-height: calc(1em + 0.5rem);\n    padding: 0;\n  }\n\n  b,\n  strong {\n    font-weight: bold;\n  }\n\n  a {\n    color: ", ";\n    text-decoration: underline;\n\n    &:hover,\n    &:focus,\n    &:active {\n      color: ", ";\n      text-decoration: none;\n    }\n\n    &:hover {\n      cursor: pointer;\n    }\n\n    &:focus {\n      outline: 0;\n    }\n  }\n\n  // Custom scrollbar\n\n\n  //media selector for mouse input devices\n  @media (pointer: fine) {\n    ::-webkit-scrollbar {\n        width: 12px;\n        height: 12px;\n    }\n\n    ::-webkit-scrollbar-track {\n        -webkit-box-shadow: inset 0 0 12px rgb(214, 214, 214);\n    }\n\n    ::-webkit-scrollbar-thumb {\n        background-color: #b5b5b5;\n        border: 2px solid #e7e7e7;\n        border-radius: 12px;\n    }\n\n    ::-webkit-scrollbar-thumb:hover {\n        background-color: #999999;\n    }\n  }\n"])), getTokenValue(THEME_TOKENS.base.fontFamily.body), getTokenValue(THEME_TOKENS.base.size.bodyText), getTokenValue(THEME_TOKENS.base.color.secondaryText), getTokenValue(THEME_TOKENS.base.color.action), getTokenValue(THEME_TOKENS.base.color.action));
createGlobalStyle(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n  :root { \n    ", "; \n  }\n"], ["\n  :root { \n    ", "; \n  }\n"])), function (_a) {
  var theme = _a.theme;
  return generateCustomProperties(theme);
});
var templateObject_1, templateObject_2;

const TRIP_TYPE = {
  flight: 'Flight',
  hotel: 'Hotel',
  car: 'Car'
};
const CHANGE_OPTION = {
  keep: 'keep',
  modify: 'modify',
  cancel: 'cancel'
};

const getCardKind = co => {
  switch (co) {
    case CHANGE_OPTION.modify:
      return 'warning';
    case CHANGE_OPTION.cancel:
      return 'danger';
    case CHANGE_OPTION.keep:
      return '';
    default:
      return '';
  }
};
const getLeftBorderColor = props => {
  if (props.pending) {
    return getTokenValue(THEME_TOKENS.base.color.neutral600);
  }
  if (props.changeOption === CHANGE_OPTION.modify) {
    return getTokenValue(THEME_TOKENS.base.color.warningBorder);
  }
  if (props.changeOption === CHANGE_OPTION.cancel) {
    return getTokenValue(THEME_TOKENS.base.color.dangerBorder);
  }
  if (props.changeOption === CHANGE_OPTION.keep) ;
  return getTokenValue(THEME_TOKENS.base.color.infoBorder);
};
const getChangeAccentColor = props => {
  const {
    changeOption,
    noAccentColor,
    pending
  } = props;
  if (noAccentColor) {
    return 'white';
  }
  if (pending) {
    return '#E8E8E8';
  }
  if (changeOption === CHANGE_OPTION.modify) {
    return getTokenValue(THEME_TOKENS.base.color.warningBackground);
  }
  if (changeOption === CHANGE_OPTION.cancel) {
    return getTokenValue(THEME_TOKENS.base.color.dangerBackground);
  }
  if (changeOption === CHANGE_OPTION.keep) {
    return getTokenValue(THEME_TOKENS.base.color.infoBackground);
  }
  return getTokenValue(THEME_TOKENS.base.color.white);
};
const getHotelAddressDisplay = addressObj => {
  const hotelStateStr = addressObj.stateCode || addressObj.state;
  const hotelStateOrCountry = (hotelStateStr === null || hotelStateStr === void 0 ? void 0 : hotelStateStr.length) === 0 ? addressObj.countryName : hotelStateStr;
  return `${addressObj.city}, ${hotelStateOrCountry}`;
};
const formatUSCurrencyNum = num => {
  const formatUSCurrency = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(num);
  return formatUSCurrency.slice(1);
};
const normalizeDate = dateOrString => {
  return dateOrString instanceof Date ? dateOrString : parseDate(dateOrString);
};

function _extends() {
  _extends = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends.apply(this, arguments);
}

const FlexColumn = styled(Flex)`
  flex-direction: column;
  gap: ${_ref => {
  let {
    gap
  } = _ref;
  return gap || 'unset';
}};
`;
const DetailsColumn = _ref2 => {
  let {
    gap = 8,
    children,
    ...rest
  } = _ref2;
  const gapVal = typeof gap === 'number' ? `${gap}px` : gap;
  return /*#__PURE__*/React.createElement(FlexColumn, _extends({
    gap: gapVal
  }, rest), children);
};
DetailsColumn.propTypes = {
  gap: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  children: PropTypes.node
};

const NearbyAirportsSmall = styled(Flex)`
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
  padding-bottom: 4px;
`;
const NearbyAirportsMedium = styled(Flex)`
  flex-wrap: wrap;
  margin-bottom: -8px;
`;
const AirportCode = styled.div`
  min-width: 38px;
  display: inline-flex;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  cursor: default;
  background-color: ${getThemeProp('global.greyPalette.200')};
  border-radius: 4px;
  padding: 1px 4px;
  margin-right: 8px;
  margin-bottom: ${_ref => {
  let {
    compact
  } = _ref;
  return compact ? '0' : '8px';
}};
`;
const AirportInfo = styled(Flex)`
  align-items: center;
  font-size: 13px;
`;
const NearbyAirports = _ref2 => {
  let {
    airports,
    compact = false
  } = _ref2;
  const includedOptions = airports.filter(a => a.include === true);
  if (includedOptions.length > 0) {
    return compact ? /*#__PURE__*/React.createElement(NearbyAirportsSmall, null, includedOptions.map(o => /*#__PURE__*/React.createElement(AirportInfo, {
      key: o.code
    }, /*#__PURE__*/React.createElement(AirportCode, {
      compact: compact
    }, o.code), o.name))) : /*#__PURE__*/React.createElement(NearbyAirportsMedium, null, includedOptions.map(o => /*#__PURE__*/React.createElement(Tooltip, {
      key: o.code,
      content: o.name,
      placement: "top"
    }, /*#__PURE__*/React.createElement(AirportInfo, null, /*#__PURE__*/React.createElement(AirportCode, {
      compact: compact
    }, o.code)))));
  }
  return null;
};
NearbyAirports.propTypes = {
  airports: PropTypes.arrayOf(PropTypes.shape({
    code: PropTypes.string,
    name: PropTypes.string,
    include: PropTypes.bool
  })),
  compact: PropTypes.bool
};

// TODO remove object modeling from these components:
// FlightMinimalView
// FlightMinimalViewCompact

const automatedActionsProps = {
  agreedToTerms: PropTypes.bool,
  changeOption: PropTypes.oneOf(Object.keys(CHANGE_OPTION)),
  componentType: PropTypes.oneOf(Object.values(TRIP_TYPE)).isRequired,
  creditCard: PropTypes.shape({
    cardType: PropTypes.string,
    cardNumber: PropTypes.string,
    cardHolderName: PropTypes.string
  }),
  currentCar: PropTypes.shape({}),
  currentFlight: PropTypes.shape({}),
  currentHotel: PropTypes.shape({}),
  isCancel: PropTypes.bool,
  newSegment: PropTypes.shape({}),
  outOfPolicy: PropTypes.bool,
  outOfPolicyDetails: PropTypes.arrayOf(PropTypes.string),
  priceAmount: PropTypes.number,
  priceLabel: PropTypes.string,
  shopRequest: PropTypes.shape({}),
  termsMessage: PropTypes.string,
  travelers: PropTypes.arrayOf(PropTypes.shape({
    displayName: PropTypes.string,
    nameNum: PropTypes.number,
    nameParts: PropTypes.shape({
      lastName: PropTypes.string
    })
  }))
};

// F L I G H T  P R O P S  ///////////////////////////

const flightViewProps = {
  data: PropTypes.shape({
    airlineCode: PropTypes.string,
    airlineName: PropTypes.string,
    departTimeLabel: PropTypes.string,
    // example: Anytime
    departs: PropTypes.string,
    // date
    arrives: PropTypes.string,
    // date
    fromAirports: PropTypes.arrayOf(PropTypes.shape({
      code: PropTypes.string,
      name: PropTypes.string,
      include: PropTypes.bool
    })),
    fromCode: PropTypes.string,
    fromName: PropTypes.string,
    toCode: PropTypes.string,
    toName: PropTypes.string,
    flightNumber: PropTypes.string,
    numStops: PropTypes.number,
    // this is used in the change form
    // TODO: create cabinLabel to use in views
    cabin: PropTypes.shape({
      label: PropTypes.string
    }),
    travelers: PropTypes.arrayOf(PropTypes.shape({
      displayName: PropTypes.string,
      nameNum: PropTypes.number,
      nameParts: PropTypes.shape({
        lastName: PropTypes.string
      })
    })),
    changeRules: PropTypes.string,
    cancelRules: PropTypes.string,
    price: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  }),
  compact: PropTypes.bool
};

// H O T E L  P R O P S  /////////////////////////////

const hotelViewProps = {
  data: PropTypes.shape({
    checkIn: PropTypes.string,
    checkOut: PropTypes.string,
    hotelName: PropTypes.string,
    guestName: PropTypes.string,
    roomType: PropTypes.string,
    thumbnail: PropTypes.string,
    hotelAddress: PropTypes.shape({
      city: PropTypes.string,
      state: PropTypes.string
    }),
    changeRules: PropTypes.string,
    cancelRules: PropTypes.string,
    price: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  }),
  compact: PropTypes.bool
};
({
  ...automatedActionsProps,
  requestedHotel: PropTypes.shape({})
});
({
  checkInDate: PropTypes.instanceOf(Date),
  checkOutDate: PropTypes.instanceOf(Date),
  hotelName: PropTypes.string,
  guestName: PropTypes.string,
  roomType: PropTypes.string,
  thumbnail: PropTypes.string,
  hotelAddress: PropTypes.shape({
    city: PropTypes.string,
    state: PropTypes.string
  }),
  pricing: PropTypes.shape({}),
  termsAndConditions: PropTypes.shape({
    header: PropTypes.string,
    priceTerms: PropTypes.arrayOf(PropTypes.string),
    terms: PropTypes.arrayOf(PropTypes.string)
  })
});

// C A R  P R O P S  /////////////////////////////////

const carViewBase = {
  carTypeCode: PropTypes.string,
  carTypeLabel: PropTypes.string,
  // example: Keep Midsize Car
  carType: PropTypes.shape({
    label: PropTypes.string
  }),
  driverName: PropTypes.string,
  dropOffDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  dropOffTimeLabel: PropTypes.string,
  dropOffLocationName: PropTypes.string,
  pickUpDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  pickUpTimeLabel: PropTypes.string,
  pickUpLocationName: PropTypes.string,
  vendorName: PropTypes.string,
  vendorCode: PropTypes.string,
  changeRules: PropTypes.string,
  cancelRules: PropTypes.string,
  price: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};
const carViewProps = {
  data: PropTypes.shape({
    ...carViewBase
  }),
  compact: PropTypes.bool
};
({
  ...automatedActionsProps,
  requestedCar: PropTypes.shape({
    ...carViewBase
  })
});
({
  ...carViewBase,
  pricing: PropTypes.shape({}),
  termsAndConditions: PropTypes.shape({
    header: PropTypes.string,
    priceTerms: PropTypes.arrayOf(PropTypes.string),
    terms: PropTypes.arrayOf(PropTypes.string)
  })
});

const FlightMinimalView = _ref => {
  var _data$from, _data$to;
  let {
    data
  } = _ref;
  const flightObj = {
    fromName: ((_data$from = data.from) === null || _data$from === void 0 ? void 0 : _data$from.shortDisplay) || data.fromName,
    fromAirports: data.fromAirports || [],
    toName: ((_data$to = data.to) === null || _data$to === void 0 ? void 0 : _data$to.shortDisplay) || data.toName,
    toAirports: data.toAirports || [],
    departDate: data.departDate || data.departs
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(DetailsColumn, {
    width: "172px",
    mr: 2
  }, /*#__PURE__*/React.createElement(Text, {
    size: 14
  }, "Depart"), /*#__PURE__*/React.createElement(Text, {
    size: 14,
    weight: "semiBold",
    lineHeight: 1.2,
    primary: true
  }, flightObj.fromName), /*#__PURE__*/React.createElement(NearbyAirports, {
    airports: flightObj.fromAirports
  })), /*#__PURE__*/React.createElement(DetailsColumn, {
    width: "172px",
    mr: 2
  }, /*#__PURE__*/React.createElement(Text, {
    size: 14
  }, "Arrive"), /*#__PURE__*/React.createElement(Text, {
    size: 14,
    weight: "semiBold",
    lineHeight: 1.2,
    primary: true
  }, flightObj.toName), /*#__PURE__*/React.createElement(NearbyAirports, {
    airports: flightObj.toAirports
  })), /*#__PURE__*/React.createElement(DetailsColumn, {
    width: "100px"
  }, /*#__PURE__*/React.createElement(Text, {
    size: 14
  }, "Date"), /*#__PURE__*/React.createElement(Text, {
    size: 14,
    weight: "semiBold",
    primary: true
  }, getShortDateSummary(normalizeDate(flightObj.departDate)))));
};
FlightMinimalView.propTypes = {
  ...flightViewProps
};

const ChangeAgainLink = styled.button`
  all: unset;
  font-size: 14px;
  font-weight: ${getTokenValue(THEME_TOKENS.base.fontWeight.semiBold)};
  color: ${getTokenValue(THEME_TOKENS.base.color.action)};
  text-decoration: underline;
  text-underline-position: under;
  cursor: pointer;

  // These print styles are for a special PDF view that gets emailed to Approvers.
  @media print {
    display: none;
  }
`;
const HotelName = styled.div`
  max-width: 295px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;
const GridParentStyled = styled.div`
  display: grid;
  grid-template-columns: ${_ref => {
  let {
    columns
  } = _ref;
  return columns;
}};
  grid-template-rows: ${_ref2 => {
  let {
    rows
  } = _ref2;
  return rows;
}};
  grid-auto-rows: ${_ref3 => {
  let {
    autoRows
  } = _ref3;
  return autoRows;
}};
  grid-column-gap: ${_ref4 => {
  let {
    columnGap
  } = _ref4;
  return columnGap;
}};
  grid-row-gap: ${_ref5 => {
  let {
    rowGap
  } = _ref5;
  return rowGap;
}};
`;
const GridParent = _ref6 => {
  let {
    columns = 'repeat(2, 1fr) repeat(3, 0)',
    rows = 'unset',
    autoRows = 'minmax(min-content, min-content)',
    columnGap = '0',
    rowGap = '0',
    children
  } = _ref6;
  return /*#__PURE__*/React.createElement(GridParentStyled, {
    columns: columns,
    rows: rows,
    autoRows: autoRows,
    columnGap: columnGap,
    rowGap: rowGap
  }, children);
};
GridParent.propTypes = {
  columns: PropTypes.string,
  rows: PropTypes.string,
  autoRows: PropTypes.string,
  columnGap: PropTypes.string,
  rowGap: PropTypes.string,
  children: PropTypes.node
};
const GridChildStyled = styled(Box)`
  grid-area: ${_ref7 => {
  let {
    area
  } = _ref7;
  return area;
}};
`;
const GridChild = _ref8 => {
  let {
    area = '1 / 1',
    children
  } = _ref8;
  return /*#__PURE__*/React.createElement(GridChildStyled, {
    area: area
  }, children);
};
GridChild.propTypes = {
  area: PropTypes.string,
  children: PropTypes.node
};

const FlightMinimalViewCompact = _ref => {
  var _data$from, _data$to;
  let {
    data
  } = _ref;
  const flightObj = {
    fromName: ((_data$from = data.from) === null || _data$from === void 0 ? void 0 : _data$from.shortDisplay) || data.fromName,
    fromAirports: data.fromAirports || [],
    toName: ((_data$to = data.to) === null || _data$to === void 0 ? void 0 : _data$to.shortDisplay) || data.toName,
    toAirports: data.toAirports || [],
    departDate: data.departDate || data.departs
  };
  const gridArea1 = '1 / 1';
  const gridArea2 = '2 / 1';
  const gridArea3 = '3 / 1';
  const gridArea4 = '1 / 2';
  const gridArea5 = '2 / 2';
  const gridArea6 = '3 / 2';
  return /*#__PURE__*/React.createElement(GridParent, {
    columns: "54px 1fr",
    rowGap: "12px"
  }, /*#__PURE__*/React.createElement(GridChild, {
    area: gridArea1
  }, /*#__PURE__*/React.createElement(Text, {
    size: 14
  }, "Depart")), /*#__PURE__*/React.createElement(GridChild, {
    area: gridArea4
  }, /*#__PURE__*/React.createElement(Text, {
    size: 14,
    lineHeight: 1.2,
    weight: "semiBold",
    primary: true
  }, flightObj.fromName), /*#__PURE__*/React.createElement(NearbyAirports, {
    airports: flightObj.fromAirports,
    compact: true
  })), /*#__PURE__*/React.createElement(GridChild, {
    area: gridArea2
  }, /*#__PURE__*/React.createElement(Text, {
    size: 14
  }, "Arrive")), /*#__PURE__*/React.createElement(GridChild, {
    area: gridArea5
  }, /*#__PURE__*/React.createElement(Text, {
    size: 14,
    lineHeight: 1.2,
    weight: "semiBold",
    primary: true
  }, flightObj.toName), /*#__PURE__*/React.createElement(NearbyAirports, {
    airports: flightObj.toAirports,
    compact: true
  })), /*#__PURE__*/React.createElement(GridChild, {
    area: gridArea3
  }, /*#__PURE__*/React.createElement(Text, {
    size: 14
  }, "Date")), /*#__PURE__*/React.createElement(GridChild, {
    area: gridArea6
  }, /*#__PURE__*/React.createElement(Text, {
    size: 14,
    weight: "semiBold",
    primary: true
  }, getShortDateSummary(normalizeDate(flightObj.departDate)))));
};
FlightMinimalViewCompact.propTypes = {
  ...flightViewProps
};

const DetailsRowSmall = styled(Flex)`
  align-items: center;
  height: 12px;
  font-size: 12px;
  gap: 8px;
  font-weight: ${getTokenValue(THEME_TOKENS.base.fontWeight.semiBold)};
  color: ${getTokenValue(THEME_TOKENS.base.color.primaryText)};
  white-space: nowrap;

  span {
    max-width: 115px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
`;

const AirportOrRail = styled.div`
  text-transform: uppercase;
`;
const CabinBrand = styled.span`
  max-width: 145px !important;
`;
const FlightDetailsSmall = _ref => {
  let {
    data,
    handleShoppingClick,
    ...rest
  } = _ref;
  return /*#__PURE__*/React.createElement(Flex, _extends({
    flexDirection: "column",
    alignSelf: "center"
  }, rest), /*#__PURE__*/React.createElement(Flex, {
    alignItems: "center"
  }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(AirlineLogo, {
    alCode: data.airlineCode,
    size: "28px"
  })), /*#__PURE__*/React.createElement(DetailsColumn, {
    ml: 2,
    gap: 5
  }, /*#__PURE__*/React.createElement(DetailsRowSmall, null, /*#__PURE__*/React.createElement(AirportOrRail, null, data.fromCode), " ", getTimeDisplayText(normalizeDate(data.departDate)), /*#__PURE__*/React.createElement(IconFlex, {
    size: 18,
    defaultColor: true
  }, /*#__PURE__*/React.createElement(IconArrowUpLong, {
    rotate: 90
  })), /*#__PURE__*/React.createElement(AirportOrRail, null, data.toCode), " ", getTimeDisplayText(normalizeDate(data.arriveDate))), /*#__PURE__*/React.createElement(DetailsRowSmall, null, /*#__PURE__*/React.createElement("span", {
    title: data.flightNumber
  }, data.flightNumber), /*#__PURE__*/React.createElement(DotSeparator, null), data.numStops === 0 ? 'Nonstop' : `${data.numStops} ${data.numStops > 1 ? 'Stops' : 'Stop'}`, /*#__PURE__*/React.createElement(DotSeparator, null), /*#__PURE__*/React.createElement(CabinBrand, {
    title: data.cabin.label
  }, data.cabin.label)))), handleShoppingClick ? /*#__PURE__*/React.createElement(Box, {
    ml: "36px",
    mt: "6px"
  }, /*#__PURE__*/React.createElement(ChangeAgainLink, {
    onClick: handleShoppingClick
  }, "Change Flight")) : null);
};
FlightDetailsSmall.propTypes = {
  ...flightViewProps
};

const FlightOriginalViewCompact = _ref => {
  let {
    data
  } = _ref;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Box, {
    p: 3
  }, /*#__PURE__*/React.createElement(FlightMinimalViewCompact, {
    data: data
  })), /*#__PURE__*/React.createElement(FlightDetailsSmall, {
    pt: 2,
    pb: 3,
    px: 3,
    data: data
  }));
};
FlightOriginalViewCompact.propTypes = {
  ...flightViewProps
};
const FlightOriginalView = _ref2 => {
  let {
    data,
    compact = false
  } = _ref2;
  return compact ? /*#__PURE__*/React.createElement(FlightOriginalViewCompact, {
    data: data
  }) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(MatchMedia.Medium, null, /*#__PURE__*/React.createElement(Box, {
    p: "20px"
  }, /*#__PURE__*/React.createElement(Flex, null, /*#__PURE__*/React.createElement(FlightMinimalView, {
    data: data
  }), /*#__PURE__*/React.createElement(FlightDetailsSmall, {
    data: data
  })))), /*#__PURE__*/React.createElement(MatchMedia.SmallAndLess, null, /*#__PURE__*/React.createElement(FlightOriginalViewCompact, {
    data: data
  })));
};
FlightOriginalView.propTypes = {
  ...flightViewProps
};

const HeaderText = styled.div`
  color: ${_ref => {
  let {
    strong
  } = _ref;
  return strong ? 'black' : getTokenValue(THEME_TOKENS.base.color.gray900);
}};
  font-size: 12px;
  text-transform: uppercase;
  font-weight: ${getTokenValue(THEME_TOKENS.base.fontWeight.semiBold)};
  letter-spacing: 1.3px;
  margin-bottom: 4px;
`;
const HeaderTextCompact = styled.div`
  color: ${_ref2 => {
  let {
    strong
  } = _ref2;
  return strong ? 'black' : getTokenValue(THEME_TOKENS.base.color.gray900);
}};
  font-size: 11px;
  text-transform: uppercase;
  font-weight: ${getTokenValue(THEME_TOKENS.base.fontWeight.semiBold)};
  letter-spacing: 1.3px;
  background-color: ${_ref3 => {
  let {
    strong
  } = _ref3;
  return strong ? getTokenValue(THEME_TOKENS.base.color.neutral400) : getTokenValue(THEME_TOKENS.base.color.neutral300);
}};
  padding: 6px;
`;
const SecondaryContent = styled(Box)`
  margin-left: auto;
  margin-bottom: -15px;
`;
const SecondaryContentCompact = styled(Box)`
  position: absolute;
  top: 8px;
  right: 0;
  padding-right: 8px;
`;
const HorizLine = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${getTokenValue(THEME_TOKENS.base.color.neutral400)};
`;
const Container = styled(Flex)`
  flex-direction: column;
  width: 100%;
  position: relative;
`;
const LineHeader = _ref4 => {
  let {
    headerText,
    secondaryContent,
    strong
  } = _ref4;
  return /*#__PURE__*/React.createElement(React.Fragment, null, secondaryContent ? /*#__PURE__*/React.createElement(SecondaryContent, null, secondaryContent) : null, /*#__PURE__*/React.createElement(Flex, {
    alignItems: "baseline"
  }, /*#__PURE__*/React.createElement(HeaderText, {
    strong: strong
  }, headerText)), /*#__PURE__*/React.createElement(HorizLine, null));
};
LineHeader.propTypes = {
  headerText: PropTypes.string,
  secondaryContent: PropTypes.node,
  strong: PropTypes.bool
};
const LineHeaderCompact = _ref5 => {
  let {
    headerText,
    secondaryContent,
    strong
  } = _ref5;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(HorizLine, null), /*#__PURE__*/React.createElement(Flex, {
    alignItems: "baseline",
    justifyContent: "space-between"
  }, /*#__PURE__*/React.createElement(HeaderTextCompact, {
    strong: strong
  }, headerText), secondaryContent ? /*#__PURE__*/React.createElement(SecondaryContentCompact, null, secondaryContent) : null));
};
LineHeaderCompact.propTypes = {
  headerText: PropTypes.string,
  secondaryContent: PropTypes.node,
  strong: PropTypes.bool
};
const TitleCaseLineHeader = _ref6 => {
  let {
    headerText,
    secondaryContent,
    compact = false,
    strong = false,
    ...rest
  } = _ref6;
  return compact ? /*#__PURE__*/React.createElement(Container, rest, /*#__PURE__*/React.createElement(LineHeaderCompact, {
    headerText: headerText,
    secondaryContent: secondaryContent,
    strong: strong
  })) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(MatchMedia.Medium, null, /*#__PURE__*/React.createElement(Container, rest, /*#__PURE__*/React.createElement(LineHeader, {
    headerText: headerText,
    secondaryContent: secondaryContent,
    strong: strong
  }))), /*#__PURE__*/React.createElement(MatchMedia.SmallAndLess, null, /*#__PURE__*/React.createElement(Container, rest, /*#__PURE__*/React.createElement(LineHeaderCompact, {
    headerText: headerText,
    secondaryContent: secondaryContent,
    strong: strong
  }))));
};
TitleCaseLineHeader.propTypes = {
  headerText: PropTypes.string,
  secondaryContent: PropTypes.node,
  compact: PropTypes.bool,
  strong: PropTypes.bool
};

const GrayscaleContainer = styled(Box)`
  width: 100%;
  filter: grayscale(1) opacity(50%);
`;
const GrayscaleArea = _ref => {
  let {
    children
  } = _ref;
  return /*#__PURE__*/React.createElement(GrayscaleContainer, null, children);
};
GrayscaleArea.propTypes = {
  children: PropTypes.node
};

const FlightChangeViewCompact = _ref => {
  let {
    data,
    changeRequest
  } = _ref;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Box, {
    p: 3
  }, /*#__PURE__*/React.createElement(FlightMinimalViewCompact, {
    data: changeRequest
  }), /*#__PURE__*/React.createElement(DetailsRowSmall, {
    mt: 3
  }, changeRequest.departTimeLabel, /*#__PURE__*/React.createElement(DotSeparator, null), changeRequest.cabinLabel)), /*#__PURE__*/React.createElement(TitleCaseLineHeader, {
    compact: true,
    mt: 2,
    headerText: "Original Flight"
  }), /*#__PURE__*/React.createElement(GrayscaleArea, null, /*#__PURE__*/React.createElement(Box, {
    p: 3
  }, /*#__PURE__*/React.createElement(FlightMinimalViewCompact, {
    data: data
  })), /*#__PURE__*/React.createElement(FlightDetailsSmall, {
    pt: 2,
    pb: 3,
    px: 3,
    data: data
  })));
};
FlightChangeViewCompact.propTypes = {
  ...flightViewProps
};
const FlightChangeView = _ref2 => {
  let {
    data,
    changeRequest,
    compact = false
  } = _ref2;
  const {
    departTimeLabel,
    cabinLabel
  } = changeRequest;
  return compact ? /*#__PURE__*/React.createElement(FlightChangeViewCompact, {
    data: data,
    changeRequest: changeRequest
  }) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(MatchMedia.Medium, null, /*#__PURE__*/React.createElement(Box, {
    p: "20px"
  }, /*#__PURE__*/React.createElement(Flex, {
    mb: "20px"
  }, /*#__PURE__*/React.createElement(FlightMinimalView, {
    data: changeRequest
  }), /*#__PURE__*/React.createElement(DetailsColumn, {
    width: "100px"
  }, /*#__PURE__*/React.createElement(Text, {
    size: 14
  }, "Time"), /*#__PURE__*/React.createElement(Text, {
    size: 14,
    weight: "semiBold",
    primary: true
  }, departTimeLabel)), /*#__PURE__*/React.createElement(DetailsColumn, {
    width: "130px"
  }, /*#__PURE__*/React.createElement(Text, {
    size: 14
  }, "Cabin"), /*#__PURE__*/React.createElement(Text, {
    size: 14,
    weight: "semiBold",
    primary: true
  }, cabinLabel))), /*#__PURE__*/React.createElement(TitleCaseLineHeader, {
    mb: 3,
    headerText: "Original Flight"
  }), /*#__PURE__*/React.createElement(GrayscaleArea, null, /*#__PURE__*/React.createElement(Flex, null, /*#__PURE__*/React.createElement(FlightMinimalView, {
    data: data
  }), /*#__PURE__*/React.createElement(FlightDetailsSmall, {
    data: data
  }))))), /*#__PURE__*/React.createElement(MatchMedia.SmallAndLess, null, /*#__PURE__*/React.createElement(FlightChangeViewCompact, {
    data: data,
    changeRequest: changeRequest
  })));
};
FlightChangeView.propTypes = {
  ...flightViewProps
};

const HotelMinimalView = _ref => {
  let {
    data
  } = _ref;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(DetailsColumn, {
    width: "172px",
    mr: 2
  }, /*#__PURE__*/React.createElement(Text, {
    size: 14
  }, "Destination"), /*#__PURE__*/React.createElement(Text, {
    size: 14,
    weight: "semiBold",
    lineHeight: 1.2,
    primary: true
  }, getHotelAddressDisplay(data.hotelAddress))), /*#__PURE__*/React.createElement(DetailsColumn, {
    width: "100px"
  }, /*#__PURE__*/React.createElement(Text, {
    size: 14
  }, "Check In"), /*#__PURE__*/React.createElement(Text, {
    size: 14,
    weight: "semiBold",
    primary: true
  }, getShortDateSummary(normalizeDate(data.checkInDate)))), /*#__PURE__*/React.createElement(DetailsColumn, {
    width: "115px"
  }, /*#__PURE__*/React.createElement(Text, {
    size: 14
  }, "Check Out"), /*#__PURE__*/React.createElement(Text, {
    size: 14,
    weight: "semiBold",
    primary: true
  }, getShortDateSummary(normalizeDate(data.checkOutDate)))));
};
HotelMinimalView.propTypes = {
  ...hotelViewProps
};

const HotelMinimalViewCompact = _ref => {
  let {
    data
  } = _ref;
  const gridArea1 = '1 / 1';
  const gridArea2 = '2 / 1';
  const gridArea3 = '3 / 1';
  const gridArea4 = '1 / 2';
  const gridArea5 = '2 / 2';
  const gridArea6 = '3 / 2';
  return /*#__PURE__*/React.createElement(GridParent, {
    columns: "84px 1fr",
    rowGap: "12px"
  }, /*#__PURE__*/React.createElement(GridChild, {
    area: gridArea1
  }, /*#__PURE__*/React.createElement(Text, {
    size: 14
  }, "Destination")), /*#__PURE__*/React.createElement(GridChild, {
    area: gridArea4
  }, /*#__PURE__*/React.createElement(Text, {
    size: 14,
    lineHeight: 1.2,
    weight: "semiBold",
    primary: true
  }, getHotelAddressDisplay(data.hotelAddress))), /*#__PURE__*/React.createElement(GridChild, {
    area: gridArea2
  }, /*#__PURE__*/React.createElement(Text, {
    size: 14
  }, "Check In")), /*#__PURE__*/React.createElement(GridChild, {
    area: gridArea5
  }, /*#__PURE__*/React.createElement(Text, {
    size: 14,
    lineHeight: 1.2,
    weight: "semiBold",
    primary: true
  }, getShortDateSummary(normalizeDate(data.checkInDate)))), /*#__PURE__*/React.createElement(GridChild, {
    area: gridArea3
  }, /*#__PURE__*/React.createElement(Text, {
    size: 14
  }, "Check Out")), /*#__PURE__*/React.createElement(GridChild, {
    area: gridArea6
  }, /*#__PURE__*/React.createElement(Text, {
    size: 14,
    weight: "semiBold",
    primary: true
  }, getShortDateSummary(normalizeDate(data.checkOutDate)))));
};
HotelMinimalViewCompact.propTypes = {
  ...hotelViewProps
};

// const RoomDescription = styled.div`
//   width: 100px;
//   overflow: hidden;
//   white-space: nowrap;
//   text-overflow: ellipsis;
// `;

const HotelDetailsSmall = _ref => {
  let {
    data,
    handleShoppingClick,
    ...rest
  } = _ref;
  return /*#__PURE__*/React.createElement(Flex, _extends({
    flexDirection: "column",
    alignSelf: "center"
  }, rest), /*#__PURE__*/React.createElement(Flex, {
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(HotelThumbnail, {
    src: data.thumbnail,
    size: "small"
  }), /*#__PURE__*/React.createElement(DetailsColumn, {
    ml: 2,
    gap: 5
  }, /*#__PURE__*/React.createElement(DetailsRowSmall, null, /*#__PURE__*/React.createElement(HotelName, null, data.hotelName)), /*#__PURE__*/React.createElement(DetailsRowSmall, null, data.guestName))), handleShoppingClick ? /*#__PURE__*/React.createElement(Box, {
    ml: "49px"
  }, /*#__PURE__*/React.createElement(ChangeAgainLink, {
    onClick: handleShoppingClick
  }, "Change Hotel")) : null);
};
HotelDetailsSmall.propTypes = {
  ...hotelViewProps
};

const HotelOriginalViewCompact = _ref => {
  let {
    data
  } = _ref;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Box, {
    p: 3
  }, /*#__PURE__*/React.createElement(HotelMinimalViewCompact, {
    data: data
  })), /*#__PURE__*/React.createElement(HotelDetailsSmall, {
    pt: 2,
    pb: 3,
    px: 3,
    data: data
  }));
};
HotelOriginalViewCompact.propTypes = {
  ...hotelViewProps
};
const HotelOriginalView = _ref2 => {
  let {
    data,
    compact = false
  } = _ref2;
  return compact ? /*#__PURE__*/React.createElement(HotelOriginalViewCompact, {
    data: data
  }) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(MatchMedia.Medium, null, /*#__PURE__*/React.createElement(Box, {
    p: "20px"
  }, /*#__PURE__*/React.createElement(Flex, null, /*#__PURE__*/React.createElement(HotelMinimalView, {
    data: data
  }), /*#__PURE__*/React.createElement(HotelDetailsSmall, {
    data: data
  })))), /*#__PURE__*/React.createElement(MatchMedia.SmallAndLess, null, /*#__PURE__*/React.createElement(HotelOriginalViewCompact, {
    data: data
  })));
};
HotelOriginalView.propTypes = {
  ...hotelViewProps
};

const HotelChangeViewCompact = _ref => {
  let {
    data,
    changeRequest
  } = _ref;
  const {
    hotelDetails
  } = changeRequest;
  const {
    shortDisplay,
    city,
    stateCode
  } = hotelDetails;
  const destinationStr = `${city}, ${stateCode}`;
  const showHotelName = destinationStr !== shortDisplay;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Box, {
    p: 3
  }, /*#__PURE__*/React.createElement(HotelMinimalViewCompact, {
    data: changeRequest
  }), showHotelName ? /*#__PURE__*/React.createElement(DetailsRowSmall, {
    mt: 3
  }, /*#__PURE__*/React.createElement(HotelName, null, shortDisplay)) : null), /*#__PURE__*/React.createElement(TitleCaseLineHeader, {
    compact: true,
    mt: 2,
    headerText: "Original Hotel"
  }), /*#__PURE__*/React.createElement(GrayscaleArea, null, /*#__PURE__*/React.createElement(Box, {
    p: 3
  }, /*#__PURE__*/React.createElement(HotelMinimalViewCompact, {
    data: data
  })), /*#__PURE__*/React.createElement(HotelDetailsSmall, {
    pt: 2,
    pb: 3,
    px: 3,
    data: data
  })));
};
HotelChangeViewCompact.propTypes = {
  ...hotelViewProps
};
const HotelChangeView = _ref2 => {
  let {
    data,
    changeRequest,
    compact = false
  } = _ref2;
  const {
    hotelDetails
  } = changeRequest;
  const {
    shortDisplay,
    city,
    stateCode
  } = hotelDetails;
  const destinationStr = `${city}, ${stateCode}`;
  const showHotelName = destinationStr !== shortDisplay;
  return compact ? /*#__PURE__*/React.createElement(HotelChangeViewCompact, {
    data: data,
    changeRequest: changeRequest
  }) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(MatchMedia.Medium, null, /*#__PURE__*/React.createElement(Box, {
    p: "20px"
  }, /*#__PURE__*/React.createElement(Flex, {
    mb: "20px"
  }, /*#__PURE__*/React.createElement(HotelMinimalView, {
    data: changeRequest
  }), showHotelName ? /*#__PURE__*/React.createElement(DetailsColumn, null, /*#__PURE__*/React.createElement(Text, {
    size: 14
  }, "Details"), /*#__PURE__*/React.createElement(Text, {
    width: 300,
    size: 14,
    titleHover: shortDisplay,
    weight: "semiBold",
    primary: true
  }, shortDisplay)) : null), /*#__PURE__*/React.createElement(TitleCaseLineHeader, {
    mb: 3,
    headerText: "Original Hotel"
  }), /*#__PURE__*/React.createElement(GrayscaleArea, null, /*#__PURE__*/React.createElement(Flex, null, /*#__PURE__*/React.createElement(HotelMinimalView, {
    data: data
  }), /*#__PURE__*/React.createElement(HotelDetailsSmall, {
    data: data
  }))))), /*#__PURE__*/React.createElement(MatchMedia.SmallAndLess, null, /*#__PURE__*/React.createElement(HotelChangeViewCompact, {
    data: data,
    changeRequest: changeRequest
  })));
};
HotelChangeView.propTypes = {
  ...hotelViewProps
};

const FlexRow = styled(Flex)`
  align-items: center;
  gap: ${_ref => {
  let {
    gap
  } = _ref;
  return gap || 'unset';
}};
  font-size: 12px;
  white-space: nowrap;
`;
const DetailsRow$1 = _ref2 => {
  let {
    gap = 8,
    children,
    ...rest
  } = _ref2;
  const gapVal = typeof gap === 'number' ? `${gap}px` : gap;
  return /*#__PURE__*/React.createElement(FlexRow, _extends({
    gap: gapVal
  }, rest), children);
};
DetailsRow$1.propTypes = {
  gap: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  children: PropTypes.node
};

const CarMinimalView = _ref => {
  let {
    data
  } = _ref;
  return /*#__PURE__*/React.createElement(DetailsRow$1, null, /*#__PURE__*/React.createElement(DetailsColumn, null, /*#__PURE__*/React.createElement(Text, {
    size: 14
  }, "Pick-Up"), /*#__PURE__*/React.createElement(Text, {
    size: 14,
    width: "225px",
    primary: true
  }, /*#__PURE__*/React.createElement("span", {
    title: data.pickUpLocationName
  }, data.pickUpLocationName)), /*#__PURE__*/React.createElement(DetailsRow$1, null, /*#__PURE__*/React.createElement(Text, {
    size: 14,
    weight: "semiBold",
    primary: true
  }, getShortDateSummary(normalizeDate(data.pickUpDate))), /*#__PURE__*/React.createElement(DotSeparator, null), /*#__PURE__*/React.createElement(Text, {
    size: 14,
    weight: "semiBold",
    primary: true
  }, getTimeDisplayText(normalizeDate(data.pickUpDate))))), /*#__PURE__*/React.createElement(DetailsColumn, null, /*#__PURE__*/React.createElement(Text, {
    size: 14
  }, "Drop-Off"), /*#__PURE__*/React.createElement(Text, {
    size: 14,
    width: "225px",
    primary: true
  }, /*#__PURE__*/React.createElement("span", {
    title: data.dropOffLocationName
  }, data.dropOffLocationName)), /*#__PURE__*/React.createElement(DetailsRow$1, null, /*#__PURE__*/React.createElement(Text, {
    size: 14,
    weight: "semiBold",
    primary: true
  }, getShortDateSummary(normalizeDate(data.dropOffDate))), /*#__PURE__*/React.createElement(DotSeparator, null), /*#__PURE__*/React.createElement(Text, {
    size: 14,
    weight: "semiBold",
    primary: true
  }, getTimeDisplayText(normalizeDate(data.dropOffDate))))));
};
CarMinimalView.propTypes = {
  ...carViewProps
};

const CarMinimalViewCompact = _ref => {
  let {
    data
  } = _ref;
  return /*#__PURE__*/React.createElement(GridParent, {
    columns: "68px 1fr",
    rowGap: "12px"
  }, /*#__PURE__*/React.createElement(GridChild, {
    area: "1 / 1"
  }, /*#__PURE__*/React.createElement(Text, {
    mt: "1px",
    size: 14
  }, "Pick-Up")), /*#__PURE__*/React.createElement(GridChild, {
    area: "1 / 2"
  }, /*#__PURE__*/React.createElement(Text, {
    pb: 1,
    size: 14,
    lineHeight: 1.2,
    primary: true
  }, data.pickUpLocationName), /*#__PURE__*/React.createElement(DetailsRow$1, null, /*#__PURE__*/React.createElement(Text, {
    size: 14,
    weight: "semiBold",
    primary: true
  }, getShortDateSummary(normalizeDate(data.pickUpDate))), /*#__PURE__*/React.createElement(DotSeparator, null), /*#__PURE__*/React.createElement(Text, {
    size: 14,
    weight: "semiBold",
    primary: true
  }, getTimeDisplayText(normalizeDate(data.pickUpDate))))), /*#__PURE__*/React.createElement(GridChild, {
    area: "2 / 1"
  }, /*#__PURE__*/React.createElement(Text, {
    mt: "1px",
    size: 14
  }, "Drop-Off")), /*#__PURE__*/React.createElement(GridChild, {
    area: "2 / 2"
  }, /*#__PURE__*/React.createElement(Text, {
    pb: 1,
    size: 14,
    lineHeight: 1.2,
    primary: true
  }, data.dropOffLocationName), /*#__PURE__*/React.createElement(DetailsRow$1, null, /*#__PURE__*/React.createElement(Text, {
    size: 14,
    weight: "semiBold",
    primary: true
  }, getShortDateSummary(normalizeDate(data.dropOffDate))), /*#__PURE__*/React.createElement(DotSeparator, null), /*#__PURE__*/React.createElement(Text, {
    size: 14,
    weight: "semiBold",
    primary: true
  }, getTimeDisplayText(normalizeDate(data.dropOffDate))))));
};
CarMinimalViewCompact.propTypes = {
  ...carViewProps
};

const DriverName = styled.div`
  max-width: 140px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;
const CarDetailsSmall = _ref => {
  let {
    data,
    handleShoppingClick,
    ...rest
  } = _ref;
  return /*#__PURE__*/React.createElement(Flex, _extends({
    flexDirection: "column",
    alignSelf: "center"
  }, rest), /*#__PURE__*/React.createElement(Flex, {
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(CarVendorLogo, {
    mb: 0,
    carCode: data.vendorCode,
    size: "60px"
  }), /*#__PURE__*/React.createElement(DetailsColumn, {
    ml: 2,
    gap: 5
  }, /*#__PURE__*/React.createElement(DetailsRowSmall, null, data.vendorName, " Car Rental"), /*#__PURE__*/React.createElement(DetailsRowSmall, null, /*#__PURE__*/React.createElement(DriverName, null, /*#__PURE__*/React.createElement("span", {
    title: data.driverName
  }, data.driverName)), /*#__PURE__*/React.createElement(DotSeparator, null), data.carTypeLabel))), handleShoppingClick ? /*#__PURE__*/React.createElement(Box, {
    ml: "68px",
    mt: "6px"
  }, /*#__PURE__*/React.createElement(ChangeAgainLink, {
    onClick: handleShoppingClick
  }, "Change Car")) : null);
};
CarDetailsSmall.propTypes = {
  ...carViewProps
};

const CarOriginalViewCompact = _ref => {
  let {
    data
  } = _ref;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Box, {
    p: 3
  }, /*#__PURE__*/React.createElement(CarMinimalViewCompact, {
    data: data
  })), /*#__PURE__*/React.createElement(CarDetailsSmall, {
    pt: 2,
    pb: 3,
    px: 3,
    data: data
  }));
};
CarOriginalViewCompact.propTypes = {
  ...carViewProps
};
const CarOriginalView = _ref2 => {
  let {
    data,
    compact = false
  } = _ref2;
  return compact ? /*#__PURE__*/React.createElement(CarOriginalViewCompact, {
    data: data
  }) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(MatchMedia.Medium, null, /*#__PURE__*/React.createElement(Box, {
    p: "20px"
  }, /*#__PURE__*/React.createElement(Flex, null, /*#__PURE__*/React.createElement(CarMinimalView, {
    data: data
  }), /*#__PURE__*/React.createElement(CarDetailsSmall, {
    ml: 3,
    data: data
  })))), /*#__PURE__*/React.createElement(MatchMedia.SmallAndLess, null, /*#__PURE__*/React.createElement(CarOriginalViewCompact, {
    data: data
  })));
};
CarOriginalView.propTypes = {
  ...carViewProps
};

const CarChangeViewCompact = _ref => {
  let {
    data,
    changeRequest
  } = _ref;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Box, {
    p: 3
  }, /*#__PURE__*/React.createElement(CarMinimalViewCompact, {
    data: changeRequest
  }), /*#__PURE__*/React.createElement(DetailsRowSmall, {
    mt: 3
  }, changeRequest.vendorName, /*#__PURE__*/React.createElement(DotSeparator, null), changeRequest.carTypeLabel)), /*#__PURE__*/React.createElement(TitleCaseLineHeader, {
    compact: true,
    mt: 2,
    headerText: "Original Car"
  }), /*#__PURE__*/React.createElement(GrayscaleArea, null, /*#__PURE__*/React.createElement(Box, {
    p: 3
  }, /*#__PURE__*/React.createElement(CarMinimalViewCompact, {
    data: data
  })), /*#__PURE__*/React.createElement(CarDetailsSmall, {
    pt: 2,
    pb: 3,
    px: 3,
    data: data
  })));
};
CarChangeViewCompact.propTypes = {
  ...carViewProps
};
const CarChangeView = _ref2 => {
  let {
    data,
    changeRequest,
    compact = false
  } = _ref2;
  return compact ? /*#__PURE__*/React.createElement(CarChangeViewCompact, {
    data: data,
    changeRequest: changeRequest
  }) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(MatchMedia.Medium, null, /*#__PURE__*/React.createElement(Box, {
    p: "20px"
  }, /*#__PURE__*/React.createElement(Flex, {
    mb: "20px"
  }, /*#__PURE__*/React.createElement(CarMinimalView, {
    data: changeRequest
  }), /*#__PURE__*/React.createElement(DetailsColumn, {
    ml: 3
  }, /*#__PURE__*/React.createElement(Text, {
    size: 14
  }, "Vendor"), /*#__PURE__*/React.createElement(Text, {
    width: 100,
    size: 14,
    titleHover: changeRequest.vendorName,
    weight: "semiBold",
    primary: true
  }, changeRequest.vendorName)), /*#__PURE__*/React.createElement(DetailsColumn, {
    ml: 3
  }, /*#__PURE__*/React.createElement(Text, {
    size: 14
  }, "Car Type"), /*#__PURE__*/React.createElement(Text, {
    width: 160,
    size: 14,
    weight: "semiBold",
    primary: true
  }, changeRequest.carTypeLabel))), /*#__PURE__*/React.createElement(TitleCaseLineHeader, {
    mb: 3,
    headerText: "Original Car"
  }), /*#__PURE__*/React.createElement(GrayscaleArea, null, /*#__PURE__*/React.createElement(Flex, null, /*#__PURE__*/React.createElement(CarMinimalView, {
    data: data
  }), /*#__PURE__*/React.createElement(CarDetailsSmall, {
    ml: 3,
    data: data
  }))))), /*#__PURE__*/React.createElement(MatchMedia.SmallAndLess, null, /*#__PURE__*/React.createElement(CarChangeViewCompact, {
    data: data,
    changeRequest: changeRequest
  })));
};
CarChangeView.propTypes = {
  ...carViewProps
};

const getTravIcon = (componentType, isRail) => {
  switch (componentType) {
    case TRIP_TYPE.flight:
      return isRail ? 'Train' : 'Plane';
    case TRIP_TYPE.hotel:
      return 'Hotel';
    case TRIP_TYPE.car:
      return 'Car';
    default:
      return 'Briefcase';
  }
};
const Header$1 = styled(Flex)`
  align-items: center;
  padding: 8px 16px;
  background-color: ${getChangeAccentColor};
  border-style: solid;
  border-color: ${getTokenValue(THEME_TOKENS.card.color.border)};
  border-width: ${_ref => {
  let {
    noAccentColor
  } = _ref;
  return noAccentColor ? '0 0 1px 0' : 0;
}};
  border-top-left-radius: ${getTokenValue(THEME_TOKENS.card.size.borderRadiusLarge)};
  border-top-right-radius: ${getTokenValue(THEME_TOKENS.card.size.borderRadiusLarge)};

  // These print styles are for a special PDF view that gets emailed to Approvers.
  @media print {
    margin-bottom: 24px;
  }
`;
const HeaderContentWrapper = styled(Flex)`
  width: 100%;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
`;
const ChangeCardHeader = _ref2 => {
  let {
    headerText = '',
    changeOption,
    componentType,
    pending,
    isRail = false,
    noAccentColor = false,
    secondaryContent
  } = _ref2;
  return /*#__PURE__*/React.createElement(Header$1, {
    changeOption: changeOption,
    noAccentColor: noAccentColor,
    pending: pending
  }, /*#__PURE__*/React.createElement(HeaderContentWrapper, null, /*#__PURE__*/React.createElement(Flex, {
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(IconLine, {
    mr: 2,
    icon: getTravIcon(componentType, isRail),
    color: getTokenValue(THEME_TOKENS.base.color.primaryText)
  }), /*#__PURE__*/React.createElement(Text, {
    size: 15,
    weight: "semiBold",
    primary: true
  }, headerText)), /*#__PURE__*/React.createElement(Box, {
    mt: secondaryContent ? [1, 0, 0] : 0
  }, secondaryContent || null)));
};
ChangeCardHeader.propTypes = {
  headerText: PropTypes.string,
  changeOption: PropTypes.oneOf(Object.keys(CHANGE_OPTION)),
  componentType: PropTypes.oneOf(Object.values(TRIP_TYPE)),
  pending: PropTypes.bool,
  isRail: PropTypes.bool,
  noAccentColor: PropTypes.bool,
  secondaryContent: PropTypes.oneOfType([PropTypes.string, PropTypes.node])
};

const getChangeCardHeader = (componentType, changeOption, isRail) => {
  switch (changeOption) {
    case CHANGE_OPTION.keep:
      return null;
    case CHANGE_OPTION.modify:
      return /*#__PURE__*/React.createElement(ChangeCardHeader, {
        headerText: `Requested ${isRail ? 'Rail' : componentType} Change`,
        componentType: componentType,
        changeOption: CHANGE_OPTION.modify,
        isRail: isRail
      });
    case CHANGE_OPTION.cancel:
      return /*#__PURE__*/React.createElement(ChangeCardHeader, {
        headerText: `Requested ${isRail ? 'Rail' : componentType} Cancellation`,
        componentType: componentType,
        changeOption: CHANGE_OPTION.cancel,
        isRail: isRail
      });
    default:
      console.warn(`${changeOption} has no ChangeCardHeader.`);
      return null;
  }
};
const ChangeViewCard = _ref => {
  let {
    tripType,
    changeOption,
    isRail,
    children
  } = _ref;
  return /*#__PURE__*/React.createElement(Card, {
    mb: "20px",
    kind: getCardKind(changeOption)
  }, getChangeCardHeader(tripType, changeOption, isRail), children);
};
ChangeViewCard.propTypes = {
  tripType: PropTypes.oneOf(Object.values(TRIP_TYPE)).isRequired,
  changeOption: PropTypes.oneOf(Object.keys(CHANGE_OPTION)),
  isRail: PropTypes.bool,
  children: PropTypes.node
};

const TravelerName$1 = styled(Box)`
  color: ${getTokenValue(THEME_TOKENS.base.color.primaryText)};
  white-space: nowrap;
  line-height: 1.2;
  font-size: 15px;
  font-weight: ${getTokenValue(THEME_TOKENS.base.fontWeight.semiBold)};
`;
const BookNumLink = styled.a`
  color: ${getTokenValue(THEME_TOKENS.base.color.action)};
  font-size: 16px;
  font-weight: ${getTokenValue(THEME_TOKENS.base.fontWeight.semiBold)};
  text-decoration: underline;
  text-underline-position: under;
`;
const TravelerList = _ref => {
  var _affectedTravelers$;
  let {
    affectedTravelers,
    bookNum,
    isAdvisorView
  } = _ref;
  const lastName = (_affectedTravelers$ = affectedTravelers[0]) === null || _affectedTravelers$ === void 0 || (_affectedTravelers$ = _affectedTravelers$.nameParts) === null || _affectedTravelers$ === void 0 ? void 0 : _affectedTravelers$.lastName;
  return /*#__PURE__*/React.createElement(Box, {
    px: [3, 3, 0],
    mb: "12px"
  }, /*#__PURE__*/React.createElement(Text, {
    mb: 3,
    size: 17
  }, `Traveler${affectedTravelers.length > 1 ? 's' : ''}`), /*#__PURE__*/React.createElement(Flex, {
    flexWrap: "wrap"
  }, affectedTravelers.map(trav => /*#__PURE__*/React.createElement(Flex, {
    mb: 2,
    mr: 3,
    alignItems: "center",
    key: trav.nameNum
  }, /*#__PURE__*/React.createElement(Box, {
    mr: 2,
    flex: "0 0 30px"
  }, /*#__PURE__*/React.createElement(Avatar, {
    size: "30px",
    bordered: true,
    initials: trav.initials,
    imgUrl: trav.profileImageUrl
  })), /*#__PURE__*/React.createElement(TravelerName$1, null, trav.displayName)))), isAdvisorView ? /*#__PURE__*/React.createElement(Flex, {
    mt: 2,
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(Text, {
    mr: 2
  }, "Booking No."), /*#__PURE__*/React.createElement(BookNumLink, {
    target: "_blank",
    href: `https://a2b.amtrav.com/trip-confirmation?bn=${bookNum}&lastName=${lastName}`
  }, bookNum)) : null);
};
TravelerList.propTypes = {
  affectedTravelers: PropTypes.arrayOf(PropTypes.shape({
    nameNum: PropTypes.number,
    initials: PropTypes.string,
    profileImageUrl: PropTypes.string,
    nameParts: PropTypes.shape({
      lastName: PropTypes.string
    })
  })),
  bookNum: PropTypes.number,
  isAdvisorView: PropTypes.bool
};
const TripComponentContent = _ref2 => {
  let {
    tripComponent,
    changeRequest,
    compact
  } = _ref2;
  const isKeepOption = changeRequest.changeOption === CHANGE_OPTION.keep;
  const isCancelOption = changeRequest.changeOption === CHANGE_OPTION.cancel;
  switch (tripComponent === null || tripComponent === void 0 ? void 0 : tripComponent.componentType) {
    case TRIP_TYPE.flight:
      if (isKeepOption || isCancelOption) {
        return /*#__PURE__*/React.createElement(FlightOriginalView, {
          data: tripComponent.current,
          compact: compact
        });
      }
      return /*#__PURE__*/React.createElement(FlightChangeView, {
        data: tripComponent.current,
        changeRequest: changeRequest,
        compact: compact
      });
    case TRIP_TYPE.hotel:
      if (isKeepOption || isCancelOption) {
        return /*#__PURE__*/React.createElement(HotelOriginalView, {
          data: tripComponent.current,
          compact: compact
        });
      }
      return /*#__PURE__*/React.createElement(HotelChangeView, {
        data: tripComponent.current,
        changeRequest: changeRequest,
        compact: compact
      });
    case TRIP_TYPE.car:
      if (isKeepOption || isCancelOption) {
        return /*#__PURE__*/React.createElement(CarOriginalView, {
          data: tripComponent.current,
          compact: compact
        });
      }
      return /*#__PURE__*/React.createElement(CarChangeView, {
        data: tripComponent.current,
        changeRequest: changeRequest,
        compact: compact
      });
    default:
      return null;
  }
};
TripComponentContent.propTypes = {
  tripComponent: PropTypes.shape({
    componentId: PropTypes.string,
    componentType: PropTypes.string,
    changeRules: PropTypes.string,
    cancelRules: PropTypes.string,
    data: PropTypes.shape({}),
    current: PropTypes.shape({})
  }),
  changeRequest: PropTypes.shape({
    changeOption: PropTypes.oneOf(Object.keys(CHANGE_OPTION))
  }),
  compact: PropTypes.bool
};
const AdvisorAssistSummary = _ref3 => {
  let {
    tripComponents,
    compact = false,
    isAdvisorView = false
  } = _ref3;
  return /*#__PURE__*/React.createElement(Flex, {
    flexDirection: "column"
  }, /*#__PURE__*/React.createElement(TravelerList, {
    affectedTravelers: tripComponents.affectedTravelers,
    bookNum: tripComponents.bn,
    isAdvisorView: isAdvisorView
  }), tripComponents.changeSummary.map(t => {
    var _t$originalComponent$;
    return /*#__PURE__*/React.createElement(ChangeViewCard, {
      key: t.originalComponent.componentId,
      tripType: t.originalComponent.componentType,
      changeOption: t.changeRequest.changeOption,
      isRail: ((_t$originalComponent$ = t.originalComponent.data) === null || _t$originalComponent$ === void 0 ? void 0 : _t$originalComponent$.isRail),
      requested: true
    }, /*#__PURE__*/React.createElement(TripComponentContent, {
      tripComponent: t.originalComponent,
      changeRequest: t.changeRequest,
      compact: compact
    }));
  }));
};
AdvisorAssistSummary.propTypes = {
  tripComponents: PropTypes.shape({
    affectedTravelers: PropTypes.arrayOf(PropTypes.shape({})),
    bn: PropTypes.number,
    changeSummary: PropTypes.arrayOf(PropTypes.shape({
      changeRequest: PropTypes.shape({
        changeOption: PropTypes.string
      })
    }))
  }),
  compact: PropTypes.bool,
  isAdvisorView: PropTypes.bool
};

// This is only used for the FullView components in AM5

const getIconColor = props => {
  switch (props.changeOption) {
    case CHANGE_OPTION.modify:
      return getTokenValue(THEME_TOKENS.base.color.warning);
    case CHANGE_OPTION.cancel:
      return getTokenValue(THEME_TOKENS.base.color.danger);
    default:
      return getTokenValue(THEME_TOKENS.base.color.primaryText);
  }
};
const PolicyContainer = styled(Flex)`
  align-items: center;
  color: ${getTokenValue(THEME_TOKENS.base.color.primaryText)};
  font-size: 14px;
  font-weight: ${getTokenValue(THEME_TOKENS.base.fontWeight.semiBold)};
  gap: 6px;
  padding: 12px;
  line-height: 1.2;
`;
const Border = styled.div`
  width: 100%;
  border-top: 1px solid ${getTokenValue(THEME_TOKENS.card.color.border)};
`;
const Icon = styled.div`
  color: ${getIconColor};
`;
const ChangeCancelPolicy = _ref => {
  let {
    cancelRules = null,
    changeRules = null,
    changeOption
  } = _ref;
  const isKeepOption = changeOption === CHANGE_OPTION.keep;
  if ((cancelRules || changeRules) && !isKeepOption) {
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Border, null), /*#__PURE__*/React.createElement(PolicyContainer, null, /*#__PURE__*/React.createElement(Icon, {
      changeOption: changeOption
    }, /*#__PURE__*/React.createElement(IconLine, {
      icon: "ExclamationCircle",
      size: "small"
    })), changeOption === CHANGE_OPTION.cancel ? cancelRules : changeRules));
  }
  return null;
};
ChangeCancelPolicy.propTypes = {
  cancelRules: PropTypes.string,
  changeRules: PropTypes.string,
  changeOption: PropTypes.string
};

const OutOfPolicyRegion = styled(Box)`
  font-size: 13px;
  background: white;
  border-radius: ${getTokenValue(THEME_TOKENS.card.size.borderRadiusSmall)};
`;
const OutOfPolicyIconRegion = styled(Flex)`
  color: ${getTokenValue(THEME_TOKENS.base.color.warning)};
`;
const OutOfPolicyLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  margin-left: 6px;
`;
const AdditionalText = styled(Box)`
  font-style: italic;
  background: ${getTokenValue(THEME_TOKENS.base.color.neutral200)};
`;
const PolicyReasonList = styled.ul`
  font-size: 13px;
  color: ${getTokenValue(THEME_TOKENS.base.color.secondaryText)};
  margin-top: 4px;
  padding-left: 16px;

  li {
    padding: 6px 0 0 6px;
    font-weight: 400;
  }
`;
const OutOfPolicy = _ref => {
  let {
    details,
    additionalText
  } = _ref;
  return /*#__PURE__*/React.createElement(OutOfPolicyRegion, {
    p: "12px"
  }, /*#__PURE__*/React.createElement(OutOfPolicyIconRegion, {
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(IconLine, {
    icon: "AlertTriangle",
    size: "small"
  }), /*#__PURE__*/React.createElement(OutOfPolicyLabel, null, "Out of Policy")), /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(PolicyReasonList, null, details.map(det => /*#__PURE__*/React.createElement("li", {
    key: det
  }, det))), additionalText ? /*#__PURE__*/React.createElement(AdditionalText, {
    mt: 3,
    p: 2
  }, additionalText) : null));
};
OutOfPolicy.propTypes = {
  details: PropTypes.arrayOf(PropTypes.string),
  additionalText: PropTypes.string
};
OutOfPolicy.defaultProps = {
  details: []
};

const pendingBackgroundColor = '#F7F7F7';
const pendingBorderColor = '#B7B7B7';
const getTermsKind = kind => {
  switch (kind) {
    case 'change':
      return 'warning';
    case 'cancel':
      return 'danger';
    case 'pending':
      return 'neutral';
    default:
      return 'warning';
  }
};
const getTermsBackgroundColor = props => {
  switch (props.kind) {
    case 'change':
      return `color-mix(in srgb, ${getTokenValue(THEME_TOKENS.base.color.warningBackground)} 50%, white 50%)`;
    case 'cancel':
      return getTokenValue(THEME_TOKENS.base.color.dangerBackground);
    case 'pending':
      return pendingBackgroundColor;
    default:
      return 'white';
  }
};
const getTermsBorderColor = props => {
  switch (props.kind) {
    case 'change':
      return getTokenValue(THEME_TOKENS.base.color.warningBorder);
    case 'cancel':
      return getTokenValue(THEME_TOKENS.base.color.dangerBorder);
    case 'pending':
      return pendingBorderColor;
    default:
      return getTokenValue(THEME_TOKENS.base.color.neutralBorder);
  }
};

const CardContainer = styled(Flex)`
  flex-direction: column;
  background-color: ${getTermsBackgroundColor};
  border-style: solid;
  border-color: ${getTermsBorderColor};
  border-width: ${props => props.compact ? '1px 0 0 0' : '1px'};
  border-radius: ${getTokenValue(THEME_TOKENS.card.size.borderRadiusMedium)};
  overflow: hidden;
`;
const Header = styled(Box)`
  padding: 8px 8px 0 8px;
`;
const ContentContainer = styled(Box)`
  color: ${_ref => {
  let {
    kind
  } = _ref;
  return kind === 'pending' ? getTokenValue(THEME_TOKENS.base.color.secondaryText) : getTokenValue(THEME_TOKENS.base.color.primaryText);
}};
`;
const FooterContainer = styled(Flex)`
  flex-direction: column;
  width: 100%;
  padding: 12px;
  background-color: white;
`;
const TermsActionCard = _ref2 => {
  let {
    kind,
    compact,
    outOfPolicy = false,
    outOfPolicyDetails = [],
    requireAcknowledge = false,
    acknowledgeText = 'I have read and understand these terms.',
    onChange = () => {},
    checked = false,
    id,
    children
  } = _ref2;
  return /*#__PURE__*/React.createElement(CardContainer, {
    kind: kind,
    compact: compact
  }, /*#__PURE__*/React.createElement(Header, null, /*#__PURE__*/React.createElement(Badge, {
    kind: getTermsKind(kind),
    transparent: true,
    iconToken: "ExclamationCircle"
  }, "Important")), /*#__PURE__*/React.createElement(ContentContainer, {
    kind: kind,
    pt: 1,
    px: 3,
    pb: 3
  }, children, outOfPolicy ? /*#__PURE__*/React.createElement(Box, {
    mt: 3
  }, /*#__PURE__*/React.createElement(OutOfPolicy, {
    details: outOfPolicyDetails
  })) : null), requireAcknowledge ? /*#__PURE__*/React.createElement(FooterContainer, null, /*#__PURE__*/React.createElement(Flex, {
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(Checkbox, {
    checked: checked,
    onChange: onChange,
    id: id,
    label: /*#__PURE__*/React.createElement(Text, {
      ml: 2,
      pt: "2px",
      size: 13,
      weight: "semiBold",
      lineHeight: 1.2,
      primary: true
    }, acknowledgeText)
  }))) : null);
};
TermsActionCard.propTypes = {
  kind: PropTypes.oneOf(['cancel', 'change', 'pending']),
  compact: PropTypes.bool,
  outOfPolicy: PropTypes.bool,
  outOfPolicyDetails: PropTypes.arrayOf(PropTypes.string),
  /**
   * Enables footer with checkbox.
   */
  requireAcknowledge: PropTypes.bool,
  acknowledgeText: PropTypes.string,
  /**
   * Handler for checkbox.
   */
  onChange: PropTypes.func,
  checked: PropTypes.bool,
  /**
   * id for checkbox.
   */
  id: PropTypes.string,
  children: PropTypes.node
};

const DateStyled = styled.span`
  color: ${getTokenValue(THEME_TOKENS.base.color.primaryText)};
  font-size: ${_ref => {
  let {
    size
  } = _ref;
  return size;
}};
`;
const weekdayMonthDayShort = date => {
  return `${dayOfWeekShortFormatter(date)}, ${monthShortFormatter(date)} ${dayOfMonthFormatter(date)}`;
};
const DateShort = _ref2 => {
  let {
    date,
    size = 14
  } = _ref2;
  const sizeVal = typeof size === 'number' ? `${size}px` : size;
  return /*#__PURE__*/React.createElement(DateStyled, {
    size: sizeVal
  }, weekdayMonthDayShort(date));
};
DateShort.propTypes = {
  date: PropTypes.instanceOf(Object),
  size: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

const TinySquareLabel = styled(Flex)`
  flex-direction: column;
  justify-content: center;
  width: 34px;
  height: 34px;
  font-size: 10px;
  font-weight: 600;
  line-height: 1.1;
  text-align: center;
  background-color: ${getThemeProp('global.accent--color')};
  border-radius: 6px;
`;
const DetailsRow = styled(Flex)`
  align-items: center;
  font-size: 12px;
  gap: 8px;
  white-space: nowrap;
`;
const PickUpDropOffSmall = _ref => {
  let {
    itinType,
    data,
    width = 225,
    ...rest
  } = _ref;
  const carDetailsObj = {};
  if (itinType === 'pickup') {
    carDetailsObj.label1 = 'Pick';
    carDetailsObj.label2 = 'Up';
    carDetailsObj.date = new Date(data.pickUpDate);
    carDetailsObj.location = data.pickUpLocationName;
  }
  if (itinType === 'dropoff') {
    carDetailsObj.label1 = 'Drop';
    carDetailsObj.label2 = 'Off';
    carDetailsObj.date = new Date(data.dropOffDate);
    carDetailsObj.location = data.dropOffLocationName;
  }
  if (itinType !== 'pickup' && itinType !== 'dropoff') {
    console.error('Data must be Drop Off or Pick Up');
  }
  return /*#__PURE__*/React.createElement(Flex, _extends({
    alignItems: "center"
  }, rest), /*#__PURE__*/React.createElement(TinySquareLabel, {
    mr: 2
  }, /*#__PURE__*/React.createElement(Box, null, carDetailsObj.label1), /*#__PURE__*/React.createElement(Box, null, carDetailsObj.label2)), /*#__PURE__*/React.createElement(DetailsColumn, {
    gap: 5
  }, /*#__PURE__*/React.createElement(Text, {
    size: 12,
    width: width,
    titleHover: carDetailsObj.location,
    weight: "semiBold"
  }, carDetailsObj.location), /*#__PURE__*/React.createElement(DetailsRow, null, /*#__PURE__*/React.createElement(DateShort, {
    size: 12,
    date: carDetailsObj.date
  }), /*#__PURE__*/React.createElement(DotSeparator, null), /*#__PURE__*/React.createElement(Flex, {
    alignItems: "baseline"
  }, /*#__PURE__*/React.createElement(Text, {
    size: 12
  }, hourMinuteFormat(carDetailsObj.date), " ", meridianFormat(carDetailsObj.date))))));
};
PickUpDropOffSmall.propTypes = {
  itinType: PropTypes.oneOf(['pickup', 'dropoff']),
  width: PropTypes.number,
  ...carViewProps
};

const getKindColor = props => {
  switch (props.kind) {
    case 'dark':
      return 'transparent';
    case 'light':
      return () => getThemeProp('global.accent--color');
    default:
      return getThemeProp('button.info--color')(props);
  }
};
const PriceContainer = styled.div`
  display: flex;
  align-items: center;
  font-size: ${_ref => {
  let {
    size
  } = _ref;
  return size;
}};
  font-weight: bold;
  background-color: ${getKindColor};
  border-radius: 6px;
  padding: 6px 8px 5px 6px;
`;
const Dollar = styled.span`
  font-size: 0.7em;
  margin-top: -2px;
  margin-right: 2px;
`;
const TotalPrice = _ref2 => {
  let {
    price,
    kind = 'light',
    size = 18
  } = _ref2;
  const sizeVal = typeof size === 'number' ? `${size}px` : size;
  return /*#__PURE__*/React.createElement(PriceContainer, {
    kind: kind,
    size: sizeVal
  }, /*#__PURE__*/React.createElement(Dollar, null, "$"), Math.round(price));
};
TotalPrice.propTypes = {
  price: PropTypes.number,
  kind: PropTypes.oneOf(['info', 'dark']),
  size: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

const ShoppingButtonWrapper = styled.div`
  margin-bottom: 12px;
  padding-top: 4px;

  ${media.Medium`
    margin-bottom: 0;
    transform: translateY(-4px);
  `};
`;
const ChangeTripError = styled(Box)`
  max-width: 470px;
  font-size: 16px;
  font-weight: 600;
  font-style: normal;
  line-height: 1.3;
`;

// B O O K I N G  B A N N E R /////////////////

const DetailsContainer = styled(Flex)`
  position: relative;
  color: ${getThemeProp('global.headings--color')};
  width: 335px;
  // this is to offset the padding caused by
  // the popover close button
  margin: -4px;
`;

const PriceWrapperPosition$1 = styled.div`
  display: inline-flex;
  position: absolute;
  right: 0;
  top: 40px;
`;
const CarDetailsPopover = _ref => {
  let {
    data
  } = _ref;
  return /*#__PURE__*/React.createElement(Popover, {
    content: /*#__PURE__*/React.createElement(DetailsContainer, {
      flexDirection: "column"
    }, /*#__PURE__*/React.createElement(PickUpDropOffSmall, {
      mr: 2,
      mb: "12px",
      itinType: "pickup",
      data: data.current
    }), /*#__PURE__*/React.createElement(PickUpDropOffSmall, {
      mr: 3,
      mb: "12px",
      itinType: "dropoff",
      data: data.current
    }), /*#__PURE__*/React.createElement(CarDetailsSmall, {
      data: data.current
    }), /*#__PURE__*/React.createElement(PriceWrapperPosition$1, null, /*#__PURE__*/React.createElement(TotalPrice, {
      price: data.current.pricing.totalUSD
    }))),
    showCloseButton: true,
    maxWidth: "375px",
    withPadding: false
  }, /*#__PURE__*/React.createElement(Button, {
    text: `View Original Car for $${Math.round(data.current.pricing.totalUSD)}`,
    size: "small",
    borderedLight: true
  }));
};
CarDetailsPopover.propTypes = {
  data: PropTypes.shape({
    current: PropTypes.shape({
      pricing: PropTypes.shape({
        totalUSD: PropTypes.number
      })
    })
  })
};

/* fop types:
C = credit card;
B = bitcoin;
S = cash;
W = western union;
A = on account;
D = Dash;
L = Litecoin;
V = virtual card;
X = agency card;
P = Pay Later;
H = Bitcoin cash;
E = Etherium;
d = Supplier Direct */

const VirtualCardIcon = styled(IconFlex)`
  color: #516aad;
`;
const CreditCardDisplay = _ref => {
  let {
    ccDetails
  } = _ref;
  let card = '';
  let priceLabelCapitalized = ``;
  if (ccDetails) {
    const {
      cardType,
      fopType,
      priceAmount,
      priceLabel,
      creditCardDisplayAs,
      hidePricing
    } = ccDetails;
    const showVirtualCard = fopType === 'V';
    card = creditCardByKey(cardType);
    if (fopType === 'C' && cardType === '') {
      console.log('⚠️ No card type was provided');
    }
    if (priceLabel.length) {
      priceLabelCapitalized = `${ccDetails.priceLabel.charAt(0).toUpperCase()}${ccDetails.priceLabel.slice(1)}`;
    }
    const cardDisplayPaddingTop = hidePricing ? 0 : '6px';
    return /*#__PURE__*/React.createElement(Flex, {
      mt: 3,
      alignItems: hidePricing ? 'center' : 'flex-start',
      flexWrap: "wrap"
    }, /*#__PURE__*/React.createElement(Box, {
      mr: 3
    }, /*#__PURE__*/React.createElement(Text, {
      mb: hidePricing ? 0 : 1,
      size: 12,
      weight: "bold"
    }, priceLabelCapitalized), hidePricing ? null : /*#__PURE__*/React.createElement(Text, {
      size: 20,
      weight: "bold"
    }, priceAmount)), showVirtualCard ? /*#__PURE__*/React.createElement(Flex, {
      pt: cardDisplayPaddingTop,
      alignItems: "center"
    }, /*#__PURE__*/React.createElement(VirtualCardIcon, {
      size: 38
    }, /*#__PURE__*/React.createElement(IconCreditCard, null)), /*#__PURE__*/React.createElement(Text, {
      ml: 2,
      size: 12
    }, "Virtual Card")) : /*#__PURE__*/React.createElement(Flex, {
      pt: cardDisplayPaddingTop,
      alignItems: "center"
    }, /*#__PURE__*/React.createElement(CreditCardLogo, {
      src: card.svg
    }), /*#__PURE__*/React.createElement(Text, {
      ml: 2,
      size: 12
    }, creditCardDisplayAs)));
  }
  return null;
};
CreditCardDisplay.propTypes = {
  ccDetails: PropTypes.shape({
    cardType: PropTypes.string,
    creditCardDisplayAs: PropTypes.string.isRequired,
    fopType: PropTypes.string,
    priceAmount: PropTypes.string.isRequired,
    priceLabel: PropTypes.string.isRequired,
    hidePricing: PropTypes.bool
  })
};

const TimeStyled = styled.div`
  font-size: 20px;
  font-weight: 300;
  letter-spacing: 0.8px;
  margin-right: 2px;
`;
const Meridian = styled.div`
  font-size: 12px;
  text-transform: lowercase;
`;
const Time = _ref => {
  let {
    date
  } = _ref;
  return /*#__PURE__*/React.createElement(Flex, {
    alignItems: "baseline"
  }, /*#__PURE__*/React.createElement(TimeStyled, null, hourMinuteFormat(date)), /*#__PURE__*/React.createElement(Meridian, null, meridianFormat(date)));
};
Time.propTypes = {
  date: PropTypes.instanceOf(Date)
};

const DepartArriveDetails = _ref => {
  let {
    airportCode,
    airportName,
    date,
    ...rest
  } = _ref;
  return /*#__PURE__*/React.createElement(DetailsColumn, rest, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(AirportCode$1, {
    code: airportCode,
    name: airportName,
    primary: true
  })), /*#__PURE__*/React.createElement(DateShort, {
    date: date
  }), /*#__PURE__*/React.createElement(Time, {
    date: date
  }));
};
DepartArriveDetails.propTypes = {
  airportCode: PropTypes.string,
  airportName: PropTypes.string,
  date: PropTypes.instanceOf(Object)
};

const Divider = styled.div`
  height: 1px;
  background-color: ${getThemeProp('global.border--color')};
  margin: 20px 0;
`;
const OriginalFlights = _ref => {
  let {
    data
  } = _ref;
  return data.currentFlights.map((cf, i) => {
    const isLast = data.currentFlights.length - 1 === i;
    return /*#__PURE__*/React.createElement("div", {
      key: cf.componentId
    }, /*#__PURE__*/React.createElement(FlightMinimalViewCompact, {
      data: cf
    }), /*#__PURE__*/React.createElement(FlightDetailsSmall, {
      mt: 3,
      data: cf
    }), !isLast && /*#__PURE__*/React.createElement(Divider, null));
  });
};
const FlightDetailsPopover = _ref2 => {
  let {
    data
  } = _ref2;
  return /*#__PURE__*/React.createElement(Popover, {
    content: /*#__PURE__*/React.createElement(DetailsContainer, {
      flexDirection: "column"
    }, /*#__PURE__*/React.createElement(OriginalFlights, {
      data: data
    })),
    showCloseButton: true,
    maxWidth: "375px",
    withPadding: false
  }, /*#__PURE__*/React.createElement(Button, {
    text: "View Original Flight",
    size: "small",
    borderedLight: true
  }));
};
FlightDetailsPopover.propTypes = {
  data: PropTypes.shape({
    currentFlights: PropTypes.arrayOf(PropTypes.shape({}))
  })
};

const TextLinkTrigger = styled.button`
  all: unset;
  display: flex;
  align-items: center;
  color: ${getTokenValue(THEME_TOKENS.base.color.primaryText)};
  font-size: 14px;
  cursor: pointer;
`;
const PassengerListContent = styled(Flex)`
  color: ${getTokenValue(THEME_TOKENS.base.color.primaryText)};
`;
const AllTravelersMessage = styled(Box)`
  color: ${getTokenValue(THEME_TOKENS.base.color.secondaryText)};
`;
const FlightPassengerList = _ref => {
  let {
    travelers,
    isRail
  } = _ref;
  const [isOpen, setIsOpen] = useState(false);
  const tripTypeLabel = isRail ? 'Rail' : 'Flight';
  const clickHandler = () => setIsOpen(!isOpen);
  const closePopover = () => setIsOpen(false);
  return /*#__PURE__*/React.createElement(Popover, {
    content: /*#__PURE__*/React.createElement(PassengerListContent, {
      py: 2,
      flexDirection: "column",
      width: "220px"
    }, travelers.map(_ref2 => {
      let {
        nameNum,
        displayName
      } = _ref2;
      return /*#__PURE__*/React.createElement(Flex, {
        mb: 1,
        key: nameNum,
        alignItems: "center"
      }, /*#__PURE__*/React.createElement(DotSeparator, {
        mr: 1
      }), /*#__PURE__*/React.createElement("strong", null, displayName));
    }), /*#__PURE__*/React.createElement(AllTravelersMessage, {
      mt: 2
    }, tripTypeLabel, " changes or cancellations will be applied to all travelers in the booking. If you need to make different changes for different travelers, please contact our support team."), /*#__PURE__*/React.createElement(Flex, {
      mt: 2,
      mx: "auto",
      width: "150px"
    }, /*#__PURE__*/React.createElement(Button, {
      fullWidth: true,
      size: "small",
      onClick: closePopover,
      text: "Close"
    }))),
    open: isOpen,
    onClickOutside: closePopover
  }, /*#__PURE__*/React.createElement(TextLinkTrigger, {
    onClick: clickHandler
  }, /*#__PURE__*/React.createElement(IconLine, {
    mr: "6px",
    icon: "InfoCircle",
    size: "small"
  }), "For All Travelers"));
};
FlightPassengerList.propTypes = {
  travelers: PropTypes.arrayOf(PropTypes.shape({
    displayName: PropTypes.string,
    nameNum: PropTypes.number,
    nameParts: PropTypes.shape({
      lastName: PropTypes.string
    })
  })),
  isRail: PropTypes.bool
};

const Footnote = _ref => {
  let {
    message
  } = _ref;
  if (message) {
    return /*#__PURE__*/React.createElement(Text, {
      mt: 3,
      ml: [3, 3, 0],
      pb: [3, 3, 0],
      size: 12
    }, "* ", message);
  }
  return null;
};
Footnote.propTypes = {
  message: PropTypes.string
};

const FullTermsLink = styled.button`
  all: unset;
  font-size: 13px;
  color: ${getThemeProp('global.link--color')};
  text-decoration: underline;
  cursor: pointer;
`;
const BulletList = styled.ul`
  li {
    list-style: none;
    position: relative;
    max-width: 400px;
    font-size: 14px;
    line-height: 18px;
    padding-left: 16px;
    margin-bottom: 16px;

    &:last-of-type {
      margin-bottom: 0;
    }

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 7px;
      width: 3px;
      height: 3px;
      border-radius: 50%;
      background-color: ${getThemeProp('global.textBody--color')};
    }
  }
`;
const FullTermsModal = _ref => {
  let {
    termsAndConditions
  } = _ref;
  const [isOpen, setIsOpen] = useState(false);
  const showPriceTerms = Boolean(termsAndConditions.priceTerms);
  const showTerms = Boolean(termsAndConditions.terms);
  // we'll get at least 1 of these 2 terms arrays so we
  // check which is empty and combine the 2 no matter what
  const priceTermsArr = showPriceTerms ? termsAndConditions.priceTerms : [];
  const termsArr = showTerms ? termsAndConditions.terms : [];
  const fullTermsArr = [...priceTermsArr, ...termsArr];
  const openModal = () => {
    setIsOpen(true);
  };
  const closeModal = () => {
    setIsOpen(false);
  };
  if (fullTermsArr.length !== 0) {
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(FullTermsLink, {
      onClick: openModal
    }, "Full Terms and Conditions"), isOpen && /*#__PURE__*/React.createElement(Modal, {
      onClose: closeModal
    }, /*#__PURE__*/React.createElement(Modal.Header, null, "Terms and Conditions"), /*#__PURE__*/React.createElement(Modal.Body, null, /*#__PURE__*/React.createElement(BulletList, null, fullTermsArr.map(term => /*#__PURE__*/React.createElement("li", {
      key: term.replace(/[^0-9A-Z]+/gi, '').slice(0, 30)
    }, term)))), /*#__PURE__*/React.createElement(Modal.Footer, null, /*#__PURE__*/React.createElement(Button, {
      onClick: closeModal,
      text: "Okay"
    }))));
  }
  return null;
};
FullTermsModal.propTypes = {
  termsAndConditions: PropTypes.shape({
    priceTerms: PropTypes.arrayOf(PropTypes.string),
    terms: PropTypes.arrayOf(PropTypes.string)
  })
};

const PriceWrapperPosition = styled.div`
  display: inline-flex;
  position: absolute;
  right: 0;
  top: 36px;
`;
const HotelDetailsPopover = _ref => {
  let {
    data
  } = _ref;
  return /*#__PURE__*/React.createElement(Popover, {
    content: /*#__PURE__*/React.createElement(DetailsContainer, {
      flexDirection: "column"
    }, /*#__PURE__*/React.createElement(HotelMinimalViewCompact, {
      data: data.current
    }), /*#__PURE__*/React.createElement(HotelDetailsSmall, {
      mt: 3,
      data: data.current
    }), /*#__PURE__*/React.createElement(PriceWrapperPosition, null, /*#__PURE__*/React.createElement(TotalPrice, {
      price: data.current.pricing.totalUSD
    }))),
    showCloseButton: true,
    maxWidth: "375px",
    withPadding: false
  }, /*#__PURE__*/React.createElement(Button, {
    text: `View Original Hotel for $${Math.round(data.current.pricing.totalUSD)}`,
    size: "small",
    borderedLight: true
  }));
};
HotelDetailsPopover.propTypes = {
  data: PropTypes.shape({
    current: PropTypes.shape({
      pricing: PropTypes.shape({
        totalUSD: PropTypes.number
      })
    })
  })
};

const Banner = styled(Flex)`
  align-items: center;
  width: 100%;
  padding: 6px 10px;
  border-style: solid;
  border-width: 2px;
  border-color: ${getThemeProp('global.danger--color')};
  background-color: ${props => lighten(getThemeProp('global.danger--color')(props), 88)};
  margin-bottom: 4px;
`;
const Warning = styled.div`
  height: 33px;
  font-size: 33px;
  color: ${getThemeProp('global.danger--color')};
  margin-right: 8px;
`;
const PartialSuccessBanner = _ref => {
  let {
    message
  } = _ref;
  return /*#__PURE__*/React.createElement(Banner, null, /*#__PURE__*/React.createElement(Warning, null, /*#__PURE__*/React.createElement(IconWarningTriangle, null)), /*#__PURE__*/React.createElement(Text, {
    size: 16,
    weight: "bold",
    lineHeight: 1.3,
    pt: "3px"
  }, message));
};
PartialSuccessBanner.propTypes = {
  message: PropTypes.string
};

const PickUpDropOffDetails = _ref => {
  let {
    itinType,
    data,
    width = 200,
    ...rest
  } = _ref;
  const carDetailsObj = {};
  if (itinType === 'pickup') {
    carDetailsObj.title = 'Pick Up';
    carDetailsObj.date = new Date(data.pickUp);
    carDetailsObj.location = data.pickUpLocationName;
  }
  if (itinType === 'dropoff') {
    carDetailsObj.title = 'Drop Off';
    carDetailsObj.date = new Date(data.dropOff);
    carDetailsObj.location = data.dropOffLocationName;
  }
  if (itinType !== 'pickup' && itinType !== 'dropoff') {
    console.error('Data must be Drop Off or Pick Up');
  }
  return /*#__PURE__*/React.createElement(DetailsColumn, _extends({
    mr: 3
  }, rest), /*#__PURE__*/React.createElement(Text, {
    primary: true
  }, carDetailsObj.title), /*#__PURE__*/React.createElement(DateShort, {
    date: carDetailsObj.date
  }), /*#__PURE__*/React.createElement(Time, {
    date: carDetailsObj.date
  }), /*#__PURE__*/React.createElement(Text, {
    size: 12,
    width: width,
    titleHover: carDetailsObj.location,
    italic: true
  }, carDetailsObj.location));
};
PickUpDropOffDetails.propTypes = {
  itinType: PropTypes.string,
  width: PropTypes.number,
  ...carViewProps
};

const TravelerName = styled(Flex)`
  color: ${getTokenValue(THEME_TOKENS.base.color.secondaryText)};
  font-size: 14px;
`;
const TravelersHeader = _ref => {
  var _travelers$;
  let {
    travelers,
    componentType,
    isRail
  } = _ref;
  const showFlightPax = componentType === TRIP_TYPE.flight;
  let hasDisplayName = false;
  if (((_travelers$ = travelers[0]) === null || _travelers$ === void 0 ? void 0 : _travelers$.displayName) !== undefined) {
    hasDisplayName = true;
  }
  return (travelers === null || travelers === void 0 ? void 0 : travelers.length) > 1 && showFlightPax ? /*#__PURE__*/React.createElement(FlightPassengerList, {
    travelers: travelers,
    isRail: isRail
  }) : /*#__PURE__*/React.createElement(TravelerName, {
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(IconLine, {
    mr: "6px",
    icon: "User",
    size: "small"
  }), hasDisplayName ? travelers[0].displayName : travelers[0]);
};
TravelersHeader.propTypes = {
  travelers: PropTypes.arrayOf(PropTypes.shape({
    displayName: PropTypes.string,
    nameNum: PropTypes.number,
    nameParts: PropTypes.shape({
      lastName: PropTypes.string
    })
  })),
  componentType: PropTypes.oneOf(Object.values(TRIP_TYPE)),
  isRail: PropTypes.bool
};

const TripDetailsButton = styled.button`
  all: unset;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  font-size: 16px;
  font-weight: bold;
  color: ${getThemeProp('global.link--color')};
  letter-spacing: 1px;
  background-color: white;
  padding: 8px;
  cursor: pointer;
  transition-property: background-color, color, border-color;
  transition-duration: 0.3s;
`;
const Next = styled.div`
  height: 18px;
  color: inherit;
  font-size: 18px;
  margin-left: 8px;
`;
const goToTripDetails = url => {
  window.location = url;
};
const TripDetailsLink = _ref => {
  let {
    url
  } = _ref;
  return /*#__PURE__*/React.createElement(TripDetailsButton, {
    onClick: () => goToTripDetails(url)
  }, "View Your Current Trip", /*#__PURE__*/React.createElement(Next, null, /*#__PURE__*/React.createElement(IconNext, null)));
};
TripDetailsLink.propTypes = {
  url: PropTypes.string
};

const ViewOnlyContainer = styled(Flex)`
  width: 100%;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
`;
const ViewRegionMain = styled(Flex)`
  flex: 0 1 320px;
`;
const ViewRegionSecondary = styled(Flex)`
  margin-left: 4px;
`;
const PriceStrike = styled(Flex)`
  text-decoration: line-through;
  font-size: 18px;
  font-weight: bold;
`;
const BannerText = styled.div`
  font-size: 16px;
`;

const VendorCompact = styled(DetailsRowSmall)`
  color: ${getTokenValue(THEME_TOKENS.base.color.primaryText)};
`;
const CarFullViewCompact = _ref => {
  let {
    data
  } = _ref;
  return /*#__PURE__*/React.createElement(Box, {
    p: "20px 0 20px 20px"
  }, /*#__PURE__*/React.createElement(Flex, null, /*#__PURE__*/React.createElement(PickUpDropOffDetails, {
    itinType: "pickup",
    data: data,
    width: 160
  }), /*#__PURE__*/React.createElement(PickUpDropOffDetails, {
    itinType: "dropoff",
    data: data,
    width: 160
  })), /*#__PURE__*/React.createElement(Flex, {
    mt: 3,
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(CarVendorLogo, {
    mb: 0,
    carCode: data.vendorCode,
    size: "60px"
  }), /*#__PURE__*/React.createElement(DetailsColumn, {
    ml: 2,
    gap: 5
  }, /*#__PURE__*/React.createElement(VendorCompact, null, data.vendorName, " Car Rental"), /*#__PURE__*/React.createElement(DetailsRowSmall, null, data.driverName, /*#__PURE__*/React.createElement(DotSeparator, null), data.carType.label))));
};
CarFullViewCompact.propTypes = {
  ...carViewProps
};
const CarFullView = _ref2 => {
  let {
    data,
    compact = false
  } = _ref2;
  return compact ? /*#__PURE__*/React.createElement(CarFullViewCompact, {
    data: data
  }) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(MatchMedia.Medium, null, /*#__PURE__*/React.createElement(ViewOnlyContainer, {
    p: 3
  }, /*#__PURE__*/React.createElement(ViewRegionMain, null, /*#__PURE__*/React.createElement(Flex, {
    alignItems: "center",
    mr: "20px"
  }, /*#__PURE__*/React.createElement(CarVendorLogo, {
    mb: 0,
    carCode: data.vendorCode,
    size: "60px"
  })), /*#__PURE__*/React.createElement(Flex, {
    mr: 3,
    flexDirection: "column"
  }, /*#__PURE__*/React.createElement(Text, {
    mb: 2,
    size: 20,
    primary: true
  }, data.vendorName, " Car Rental"), /*#__PURE__*/React.createElement(Text, {
    mb: 3,
    size: 18
  }, data.carType.label), /*#__PURE__*/React.createElement(Flex, {
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(Text, null, "Driver:\xA0"), /*#__PURE__*/React.createElement(Text, {
    width: 175,
    titleHover: data.driverName
  }, data.driverName)))), /*#__PURE__*/React.createElement(ViewRegionSecondary, {
    flexDirection: ['column', 'column', 'row', 'row', 'row']
  }, /*#__PURE__*/React.createElement(PickUpDropOffDetails, {
    mb: [3, 3, 0, 0, 0],
    itinType: "pickup",
    data: data
  }), /*#__PURE__*/React.createElement(PickUpDropOffDetails, {
    itinType: "dropoff",
    data: data
  })))), /*#__PURE__*/React.createElement(MatchMedia.SmallAndLess, null, /*#__PURE__*/React.createElement(CarFullViewCompact, {
    data: data
  })));
};
CarFullView.propTypes = {
  ...carViewProps
};

const AirlineCompact = styled(DetailsRowSmall)`
  color: ${getTokenValue(THEME_TOKENS.base.color.primaryText)};
`;
const FlightFullViewCompact = _ref => {
  let {
    data
  } = _ref;
  return /*#__PURE__*/React.createElement(Box, {
    p: "20px 0 20px 20px"
  }, /*#__PURE__*/React.createElement(Flex, null, /*#__PURE__*/React.createElement(DepartArriveDetails, {
    airportCode: data.fromCode,
    airportName: data.fromName,
    date: new Date(data.departs),
    mr: "44px"
  }), /*#__PURE__*/React.createElement(DepartArriveDetails, {
    airportCode: data.toCode,
    airportName: data.toName,
    date: new Date(data.arrives)
  })), /*#__PURE__*/React.createElement(Flex, {
    mt: 3,
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(AirlineLogo, {
    alCode: data.airlineCode,
    size: "28px"
  }), /*#__PURE__*/React.createElement(DetailsColumn, {
    ml: 2,
    gap: 5
  }, /*#__PURE__*/React.createElement(AirlineCompact, null, data.airlineName, " ", /*#__PURE__*/React.createElement("span", null, data.flightNumber)), /*#__PURE__*/React.createElement(DetailsRowSmall, null, data.numStops === 0 ? 'Nonstop' : `${data.numStops} ${data.numStops > 1 ? 'Stops' : 'Stop'}`, /*#__PURE__*/React.createElement(DotSeparator, null), data.cabin.label))));
};
FlightFullViewCompact.propTypes = {
  ...flightViewProps
};
const FlightFullView = _ref2 => {
  let {
    data,
    compact = false
  } = _ref2;
  return compact ? /*#__PURE__*/React.createElement(FlightFullViewCompact, {
    data: data
  }) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(MatchMedia.Medium, null, /*#__PURE__*/React.createElement(ViewOnlyContainer, {
    p: 3,
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(ViewRegionMain, null, /*#__PURE__*/React.createElement(Flex, {
    alignItems: "center",
    mr: "24px"
  }, /*#__PURE__*/React.createElement(AirlineLogo, {
    alCode: data.airlineCode,
    size: "56px"
  })), /*#__PURE__*/React.createElement(DepartArriveDetails, {
    airportCode: data.fromCode,
    airportName: data.fromName,
    date: new Date(data.departs),
    mr: "44px"
  }), /*#__PURE__*/React.createElement(DepartArriveDetails, {
    airportCode: data.toCode,
    airportName: data.toName,
    date: new Date(data.arrives)
  })), /*#__PURE__*/React.createElement(ViewRegionSecondary, {
    alignItems: "center",
    justifyContent: [null, null, 'space-between']
  }, /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(Flex, {
    mb: 2
  }, /*#__PURE__*/React.createElement(Text, {
    mr: 2,
    primary: true
  }, data.airlineName), /*#__PURE__*/React.createElement(Text, {
    width: 125,
    titleHover: data.flightNumber,
    primary: true
  }, data.flightNumber)), /*#__PURE__*/React.createElement(Box, {
    mb: 2
  }, data.numStops === 0 ? 'Nonstop' : `${data.numStops} ${data.numStops > 1 ? 'Stops' : 'Stop'}`), /*#__PURE__*/React.createElement("div", null, data.cabin.label))))), /*#__PURE__*/React.createElement(MatchMedia.SmallAndLess, null, /*#__PURE__*/React.createElement(FlightFullViewCompact, {
    data: data
  })));
};
FlightFullView.propTypes = {
  ...flightViewProps
};

const RoomDescription = styled.div`
  width: 300px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;
const HotelFullViewCompact = _ref => {
  let {
    data
  } = _ref;
  return /*#__PURE__*/React.createElement(Box, {
    p: "20px 0 20px 20px"
  }, /*#__PURE__*/React.createElement(DetailsColumn, {
    mr: 3
  }, /*#__PURE__*/React.createElement(Text, {
    size: 20,
    primary: true
  }, getHotelAddressDisplay(data.hotelAddress)), /*#__PURE__*/React.createElement(Text, {
    size: 16,
    lineHeight: 1.2,
    primary: true
  }, data.hotelName), /*#__PURE__*/React.createElement(Flex, {
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(DateShort, {
    date: parseDate(data.arrives)
  }), " \u2014 ", /*#__PURE__*/React.createElement(DateShort, {
    date: parseDate(data.departs)
  }))), /*#__PURE__*/React.createElement(Flex, {
    mt: 3,
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(HotelThumbnail, {
    src: data.thumbnail,
    size: "small"
  }), /*#__PURE__*/React.createElement(DetailsColumn, {
    ml: 2,
    gap: 5
  }, data.roomType ? /*#__PURE__*/React.createElement(DetailsRowSmall, null, /*#__PURE__*/React.createElement(RoomDescription, null, data.roomType)) : null, /*#__PURE__*/React.createElement(DetailsRowSmall, null, data.guestName))));
};
HotelFullViewCompact.propTypes = {
  ...hotelViewProps
};
const HotelFullView = _ref2 => {
  let {
    data,
    compact = false
  } = _ref2;
  return compact ? /*#__PURE__*/React.createElement(HotelFullViewCompact, {
    data: data
  }) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(MatchMedia.Medium, null, /*#__PURE__*/React.createElement(ViewOnlyContainer, {
    p: 3
  }, /*#__PURE__*/React.createElement(ViewRegionMain, null, /*#__PURE__*/React.createElement(Flex, {
    alignItems: "center",
    mr: "20px"
  }, /*#__PURE__*/React.createElement(HotelThumbnail, {
    src: data.thumbnail
  })), /*#__PURE__*/React.createElement(DetailsColumn, {
    mr: 3
  }, /*#__PURE__*/React.createElement(Text, {
    size: 20,
    primary: true
  }, getHotelAddressDisplay(data.hotelAddress)), /*#__PURE__*/React.createElement(Text, {
    size: 16,
    lineHeight: 1.2,
    primary: true
  }, data.hotelName), /*#__PURE__*/React.createElement(Flex, {
    mb: 2,
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(Text, null, "Guest:\xA0"), /*#__PURE__*/React.createElement(Text, {
    width: 175,
    titleHover: data.guestName
  }, data.guestName)))), /*#__PURE__*/React.createElement(ViewRegionSecondary, {
    flexDirection: "column"
  }, /*#__PURE__*/React.createElement(Flex, {
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(DateShort, {
    size: 18,
    date: parseDate(data.arrives)
  }), " \u2014", ' ', /*#__PURE__*/React.createElement(DateShort, {
    size: 18,
    date: parseDate(data.departs)
  })), data.roomType ? /*#__PURE__*/React.createElement(Text, {
    mt: 2,
    width: 300,
    titleHover: data.roomType
  }, data.roomType) : null))), /*#__PURE__*/React.createElement(MatchMedia.SmallAndLess, null, /*#__PURE__*/React.createElement(HotelFullViewCompact, {
    data: data
  })));
};
HotelFullView.propTypes = {
  ...hotelViewProps
};

export { AdvisorAssistSummary, BannerText, CHANGE_OPTION, CarDetailsPopover, CarDetailsSmall, CarFullView, CarMinimalView, CarMinimalViewCompact, ChangeCancelPolicy, ChangeCardHeader, ChangeTripError, ChangeViewCard, CreditCardDisplay, DateShort, DepartArriveDetails, DetailsColumn, DetailsContainer, DetailsRow$1 as DetailsRow, DetailsRowSmall, FlightDetailsPopover, FlightDetailsSmall, FlightFullView, FlightMinimalView, FlightMinimalViewCompact, FlightPassengerList, Footnote, FullTermsModal, GrayscaleArea, HotelDetailsPopover, HotelDetailsSmall, HotelFullView, HotelMinimalView, HotelMinimalViewCompact, PartialSuccessBanner, PickUpDropOffDetails, PickUpDropOffSmall, PriceStrike, ShoppingButtonWrapper, TRIP_TYPE, TermsActionCard, Time, TitleCaseLineHeader, TotalPrice, TravelersHeader, TripDetailsLink, ViewOnlyContainer, ViewRegionMain, ViewRegionSecondary, carViewProps, flightViewProps, formatUSCurrencyNum, getChangeAccentColor, getHotelAddressDisplay, getKindColor, getLeftBorderColor, hotelViewProps };
//# sourceMappingURL=index.esm.js.map
